import OpenedCaseCard from "../components/cases/OpenedCaseCard";
import React, { useEffect, useRef, useState } from "react";
import AIActionCenter from "../components/AIActionCenter";
import AccountInspector from "../components/AccountInspector";

import {
  getClients,
  getCreditReports,
  uploadCreditReport,
  getCreditReportAccounts,
  deleteCreditReportAccounts,
  generateLetter,
  updateCreditReportAccountLetter,
  generateAllAccountDisputes,
  getLetterPdfUrl,
  getCreditCase,
  createCreditCase,
} from "../api";

export default function CreditReports() {
  const [clients, setClients] = useState([]);
  const [, setReports] = useState([]);
  const [openedCase, setOpenedCase] = useState(null);

  const [reportStats, setReportStats] = useState({
    total: 0,
    collections: 0,
    chargeOffs: 0,
    inquiries: 0,
    highPriority: 0,

    protect: 0,
    disputeCandidates: 0,
    averageConfidence: 0,
  });

  const [auditSummary, setAuditSummary] = useState({
    collections: 0,
    chargeOffs: 0,
    inquiries: 0,
    riskLevel: "Low",
  });

  const [selectedClientId, setSelectedClientId] = useState("");
  const [bureau, setBureau] = useState("Experian");
  const [selectedFile, setSelectedFile] = useState(null);

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generationProgress, setGenerationProgress] = useState("");
  const bulkGenerationLock = useRef(false);

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [accountFilter, setAccountFilter] = useState("all");

  const [selectedAccount, setSelectedAccount] = useState(null);
  const [inspectorOpen, setInspectorOpen] = useState(false);

  useEffect(() => {
    const caseId = localStorage.getItem("open_case_id");

    if (!caseId) return;

    getCreditCase(caseId)
      .then((data) => {
        if (!data || !data.id) {
          throw new Error("Saved case could not be loaded.");
        }

        setOpenedCase(data);
        localStorage.removeItem("open_case_id");
      })
      .catch((err) => {
        console.error("Failed to restore saved case:", err);
        setOpenedCase(null);
      });
  }, []);

  async function loadData() {
    try {
      setLoading(true);

      const clientsData = await getClients();
      const reportsData = await getCreditReports();

      setClients(Array.isArray(clientsData) ? clientsData : []);     

      const accountData = selectedClientId
        ? await getCreditReportAccounts(selectedClientId)
        : await getCreditReportAccounts();

      const cleanReports = Array.isArray(reportsData) 
        ? reportsData 
        : [];      

      const cleanAccounts = Array.isArray(accountData)
        ? accountData
        : [];

      const getAccountSearchText = (account) =>
        [
          account?.account_type,
          account?.type,
          account?.status,
          account?.remarks,
          account?.strategy,
          account?.review_reason,
          ...(Array.isArray(account?.flags) ? account.flags : []),
          ...(Array.isArray(account?.issues) ? account.issues : []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

      const isCollectionAccount = (account) =>
        getAccountSearchText(account).includes("collection");

      const isChargeOffAccount = (account) => {
        const value = getAccountSearchText(account);

        return (
          value.includes("charge-off") ||
          value.includes("charged-off") ||
          value.includes("charge off") ||
          value.includes("chargeoff")
        );
      };

      const isInquiryAccount = (account) =>
        getAccountSearchText(account).includes("inquiry");



     setReports(cleanReports);
     setAccounts(cleanAccounts);

   setReportStats({
     total: cleanAccounts.length,

     collections: cleanAccounts.filter(
       isCollectionAccount
     ).length,
     chargeOffs: cleanAccounts.filter(
       isChargeOffAccount
     ).length,
     inquiries: cleanAccounts.filter(
       isInquiryAccount
     ).length,

     highPriority: cleanAccounts.filter(
       (r) =>
         r.priority === "Critical" ||
         r.priority === "High"
     ).length,

     protect: cleanAccounts.filter(
       (r) => r.protect_account
    ).length,

    disputeCandidates: cleanAccounts.filter(
      (r) => r.dispute_candidate
    ).length,

    averageConfidence:
      cleanAccounts.length > 0
        ? Math.round(
            cleanAccounts.reduce(
              (sum, r) =>
                sum + (r.confidence_score || 0),
              0
            ) / cleanAccounts.length
          )
        : 0,
  });

     const riskScore =
     cleanAccounts.filter((r) =>
       String(r.status || "").toLowerCase().includes("high")
     ).length +
     cleanAccounts.filter(isCollectionAccount).length +
     cleanAccounts.filter(isChargeOffAccount).length;

   setAuditSummary({
     collections: cleanAccounts.filter(
       isCollectionAccount
     ).length,
     chargeOffs: cleanAccounts.filter(
       isChargeOffAccount
     ).length,
     inquiries: cleanAccounts.filter(
       isInquiryAccount
     ).length,
     riskLevel: riskScore >= 6 ? "High" : riskScore >= 3 ? "Medium" : "Low",
   });

    } catch (err) {
      setError(err?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps              
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId]);

  async function handleUpload(e) {
    e.preventDefault();

    if (!selectedFile) {
      setError("Please select a report file");
      return;
    }

    try {
      setUploading(true);
      setError("");
      setSuccessMessage("");

      const formData = new FormData();

      formData.append("file", selectedFile);
      formData.append("client_id", selectedClientId);
      formData.append("bureau", bureau);

      await uploadCreditReport(formData);

      setSuccessMessage("Credit report uploaded successfully");

      setSelectedFile(null);

      const input = document.getElementById("credit-report-file");

      if (input) {
        input.value = "";
      }

      await loadData();
    } catch (err) {
      setError(err?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function handleDeleteImportedAccounts() {
    if (!selectedClientId) {
      setError("Please select a client first");
      return;
    }

    const confirmed = window.confirm(
      "Delete all imported credit-report accounts for this client? " +
        "The client, letters, disputes, and other records will not be deleted."
    );

    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccessMessage("");

      await deleteCreditReportAccounts(selectedClientId);

      setSuccessMessage(
        "Imported credit-report accounts deleted successfully"
      );

      await loadData();
    } catch (err) {
      setError(
        err?.message || "Failed to delete imported accounts"
      );
    } finally {
      setLoading(false);
    }
  }

  function getSmartAudit(account) {
    const type = String(account.account_type || "").toLowerCase();

    if (type.includes("collection")) {
      return {
        risk: "Medium",
        detected: [
          "Ownership verification may be required",
          "Chain of assignment should be requested",
          "Collector authority to report should be verified",
        ],
        strategy: "Send debt validation and bureau dispute requesting complete account records.",
        nextLetter: "General Dispute",
      };
    }

    if (type.includes("charge")) {
      return {
        risk: "High",
        detected: [
          "Balance reporting should be verified",
          "Payment history should be reviewed for accuracy",
          "Charge-off reporting should be validated",
        ],
        strategy: "Request verification of balance, payment history, charge-off date, and reporting accuracy.",
        nextLetter: "Method of Verification",
      };
    }

    if (type.includes("inquiry")) {
      return {
        risk: "Low",
        detected: [
          "Permissible purpose should be verified",
          "Consumer authorization may be required",
        ],
        strategy: "Challenge inquiry if no permissible purpose or authorization exists.",
        nextLetter: "Inquiry Dispute",
      };
    }

    return {
      risk: String(account.status || "").toLowerCase().includes("high") ? "High" : "Review",
      detected: ["Manual review recommended"],
      strategy: "Review account details and select the appropriate dispute strategy.",
      nextLetter: "General Dispute",
    };
  }

  function getMetro2Violations(account) {
    const type = String(account.account_type || "").toLowerCase();
    const status = String(account.status || "").toLowerCase();

    if (type.includes("charge")) {
      return [
        "Verify charge-off date reporting",
        "Review balance accuracy after charge-off",
        "Check payment history consistency",
        "Confirm account status and remarks are accurate",
      ];
    }

    if (type.includes("collection")) {
      return [
        "Verify original creditor and ownership chain",
        "Check collection balance accuracy",
        "Confirm date opened and reporting dates",
        "Verify collector has authority to report",
      ];
    }

    if (type.includes("inquiry")) {
      return [
        "Verify permissible purpose",
        "Confirm consumer authorization",
        "Check inquiry date accuracy",
      ];
    }

    if (status.includes("high")) {
      return [
        "Manual Metro 2 review recommended",
        "Verify account status, balance, and payment history",
      ];
    }

    return ["No automatic Metro 2 flags detected"];
  }

  function getFcraViolations(account) {
    const type = String(account.account_type || "").toLowerCase();
    const status = String(account.status || "").toLowerCase();

    if (type.includes("charge")) {
      return {
        confidence: 88,
        violations: [
          "Possible inaccurate balance reporting",
          "Charge-off reporting should be verified",
          "Payment history accuracy should be reviewed",
          "Furnisher accuracy obligations may apply",
        ],
        laws: ["FCRA 611", "FCRA 623(a)(1)", "FCRA 623(b)"],
      };
    }

    if (type.includes("collection")) {
      return {
        confidence: 84,
        violations: [
          "Possible missing debt validation",
          "Ownership and collection authority should be verified",
          "Collection balance accuracy should be reviewed",
          "Original creditor reporting should be compared",
        ],
        laws: ["FCRA 611", "FCRA 623", "FDCPA 1692g"],
      };
    }

    if (type.includes("inquiry")) {
      return {
        confidence: 76,
        violations: [
          "Permissible purpose should be verified",
          "Consumer authorization should be confirmed",
          "Inquiry date and source should be reviewed",
        ],
        laws: ["FCRA 604", "FCRA 611"],
      };
    }

    return {
      confidence: status.includes("high") ? 70 : 50,
      violations: ["Manual FCRA review recommended"],
      laws: ["FCRA 611"],
    };
  }

  function getCfpbRecommendation(account, audit, fcra) {
    const type = String(account.account_type || "").toLowerCase();
    const status = String(account.status || "").toLowerCase();

    if (audit.risk === "High" || fcra.confidence >= 85) {
      return {
        recommend: true,
        urgency: "High",
        reason:
          "High-risk reporting issues detected. If the bureau or furnisher verifies without providing complete proof, prepare CFPB escalation.",
        complaintType:
          type.includes("charge")
            ? "Incorrect information on credit report"
            : "Problem with a company's investigation into an existing problem",
      };
    }

    if (type.includes("collection") || status.includes("needs")) {
      return {
        recommend: true,
        urgency: "Medium",
        reason:
          "Collection ownership or validation issues may require escalation if verification is incomplete.",
        complaintType: "Debt collection: attempts to collect debt not owed or not verified",
      };
    }

  return {
    recommend: false,
    urgency: "Low",
    reason:
      "CFPB escalation is not recommended yet. Complete the initial dispute round first.",
    complaintType: "Not recommended yet",
  };
}

  function getLetterRoundPlan(account, audit, cfpb) {
    const type = String(account.account_type || "").toLowerCase();

    if (cfpb.urgency === "High") {
    return {
      currentRound: 3,
      currentLetter: "Escalation Dispute",
      nextRound: 4,
      nextLetter: "CFPB Complaint",
      waitDays: 30,
      strategy:
        "Escalate if the bureau or furnisher verifies without providing complete documentation.",
    };
  }

  if (type.includes("charge")) {
    return {
      currentRound: 2,
      currentLetter: "Method of Verification",
      nextRound: 3,
      nextLetter: "Escalation Dispute",
      waitDays: 30,
      strategy:
        "Request verification method, payment history, charge-off date, balance, and reporting accuracy.",
    };
  }

  if (type.includes("collection")) {
    return {
      currentRound: 1,
      currentLetter: "General Dispute",
      nextRound: 2,
      nextLetter: "Debt Validation / Method of Verification",
      waitDays: 30,
      strategy:
        "Start with validation and ownership verification. Escalate if collector cannot prove authority.",
    };
  }

  if (type.includes("inquiry")) {
    return {
      currentRound: 1,
      currentLetter: "Inquiry Dispute",
      nextRound: 2,
      nextLetter: "Permissible Purpose Escalation",
      waitDays: 30,
      strategy:
        "Challenge authorization and permissible purpose. Escalate if inquiry is verified without proof.",
    };
  }

  return {
    currentRound: 1,
    currentLetter: audit.nextLetter || "General Dispute",
    nextRound: 2,
    nextLetter: "Method of Verification",
    waitDays: 30,
    strategy:
      "Begin with general dispute and advance based on bureau response.",
  };
}

  function getLitigationAnalysis(account, fcra, cfpb) {
    const type = String(account.account_type || "").toLowerCase();

    if (cfpb.urgency === "High" || fcra.confidence >= 88) {
      return {
        risk: "High",
        strength: "Strong",
        causes: [
          "FCRA 611 - reinvestigation accuracy",
          "FCRA 623(a)(1) - inaccurate furnishing",
          "FCRA 623(b) - furnisher investigation duties",
        ],
        recommendation:
          "Prepare litigation file if bureau or furnisher verifies without complete documentation.",
      };
    }

    if (type.includes("collection")) {
      return {
        risk: "Medium",
        strength: "Moderate",
        causes: [
          "FCRA 611 - bureau reinvestigation",
          "FCRA 623 - furnisher accuracy duties",
          "FDCPA 1692g - debt validation",
          "FDCPA 1692e - false or misleading collection activity",
        ],
        recommendation:
          "Demand validation and preserve evidence if collector cannot prove ownership or authority.",
      };
    }

    if (type.includes("inquiry")) {
      return {
        risk: "Low",
        strength: "Developing",
        causes: [
          "FCRA 604 - permissible purpose",
          "FCRA 611 - reinvestigation accuracy",
        ],
        recommendation:
          "Continue dispute rounds and document whether the inquiry was verified without proof.",
      };
    }

    return {
      risk: "Review",
      strength: "Unknown",
      causes: ["Manual legal review recommended"],
      recommendation:
        "Continue gathering documentation before evaluating litigation strategy.",
      };
    }  

  function getArbitrationAnalysis(account, litigation) {
    const type = String(account.account_type || "").toLowerCase();
    const creditor = String(account.creditor || "").toLowerCase();

    if (
      creditor.includes("capital one") ||
      creditor.includes("discover") ||
      creditor.includes("synchrony") ||
      creditor.includes("citi") ||
      creditor.includes("chase")
    ) {
      return {
        strength: "High",
        forum: "AAA / JAMS Review",
        clauses: [
          "Possible consumer arbitration clause",
          "Review original cardholder agreement",
          "Check opt-out and delegation language",
        ],
        recommendation:
          "Review the original agreement for arbitration rights before litigation escalation.",
      };
    }

    if (type.includes("collection")) {
      return {
        strength: "Medium",
        forum: "Original Creditor Agreement Review",
        clauses: [
          "Collection account may inherit original creditor agreement",
          "Chain of assignment should be reviewed",
          "Arbitration rights may depend on original contract terms",
        ],
        recommendation:
          "Request complete account records and assignment documents before arbitration review.",
      };
    }

    if (litigation.risk === "High") {
      return {
        strength: "Medium",
        forum: "Consumer Contract Review",
        clauses: [
          "Potential claim may require contract review",
          "Check whether arbitration clause applies",
          "Preserve all dispute and investigation evidence",
        ],
        recommendation:
          "Review account agreement before preparing litigation package.",
      };
    }

    return {
      strength: "Low",
      forum: "Not Recommended Yet",
      clauses: [
        "Complete dispute rounds first",
        "Preserve bureau and furnisher responses",
      ],
      recommendation:
        "Arbitration review is not recommended until stronger violations are documented.",
    };
  }

  function getEvidenceVault(account, litigation, arbitration) {
    const type = String(account.account_type || "").toLowerCase();

    const missingDocuments = [];

    if (type.includes("charge")) {
      missingDocuments.push(
        "Complete payment history",
        "Charge-off calculation records",
        "Account agreement",
        "Furnisher investigation results"
      );
    }

    if (type.includes("collection")) {
      missingDocuments.push(
        "Debt validation letter",
        "Chain of assignment",
        "Original creditor records",
        "Collector authority to report"
      );
    }

    if (type.includes("inquiry")) {
      missingDocuments.push(
        "Permissible purpose proof",
        "Consumer authorization",
        "Inquiry source documentation"
      );
    }

    return {
      caseReady:
        litigation.risk === "High" ||
        arbitration.strength === "High",
      strength:
        litigation.risk === "High"
          ? "Strong"
          : arbitration.strength === "High"
          ? "Moderate"
          : "Developing",
      missingDocuments,
      nextEvidenceStep:
        missingDocuments.length > 0
          ? "Collect missing proof documents before escalation."
          : "Evidence file appears complete for review.",
    };
  } 

  function getCaseValue(account, litigation, arbitration, evidence) {
    if (
      litigation.risk === "High" &&
      arbitration.strength === "High"
    ) {
      return {
        value: "$1,000 - $10,000+",
        strength: "Strong",
        attorney: "Recommended",
        damages: [
          "FCRA statutory damages",
          "Actual damages",
          "Punitive damages",
          "Attorney fees"
        ]
      };
    }

    if (litigation.risk === "High") {
      return {
        value: "$1,000 - $5,000",
        strength: "Moderate",
        attorney: "Possibly",
        damages: [
          "FCRA damages",
          "Actual damages"
        ]
      };
    }

    return {
      value: "Developing",
      strength: "Low",
      attorney: "Not yet",
      damages: [
        "Continue documentation"
      ]
    };
  }


  function getReadinessScore(
    audit,
    fcra,
    cfpb,
    litigation,
    evidence
  ) {
    let score = 0;

    // AI Risk
    if (audit.risk === "Low") score += 10;
    if (audit.risk === "Medium") score += 20;
    if (audit.risk === "High") score += 30;

    // FCRA confidence
    score += Math.floor(fcra.confidence / 4);

    // CFPB urgency
    if (cfpb.urgency === "Medium") score += 15;
    if (cfpb.urgency === "High") score += 25;

    // Litigation
    if (litigation.risk === "Medium") score += 15;
    if (litigation.risk === "High") score += 30;

    // Evidence strength
    if (evidence.strength === "Developing") score += 10;
    if (evidence.strength === "Strong") score += 25;

    // Keep within 100
    score = Math.min(score, 100);

    let rating = "Developing";

    if (score >= 80) {
      rating = "Lawsuit Ready";
    } else if (score >= 60) {
      rating = "Strong";
    } else if (score >= 40) {
      rating = "Moderate";
    }

    return {
      score,
      rating,
    };
  }

  function getAttorneyDemandEngine(
    account,
    fcra,
    litigation,
    evidence,
    caseValue
  ) {
    const type = String(account.account_type || "").toLowerCase();

    if (
      litigation.risk === "High" &&
      evidence.caseReady
    ) {
      return {
        recommended: "YES",
        demandLevel: "High",
        demandAmount: caseValue.value,
        responseDays: 15,
        strategy:
          "Send pre-litigation attorney demand after documenting bureau and furnisher verification failures.",
        leverage: [
          "High litigation risk",
          "Strong FCRA claims",
          "Evidence file is case-ready",
          "Attorney fees may be recoverable",
        ],
      };
    }

    if (
      type.includes("collection") ||
      fcra.confidence >= 80
    ) {
      return {
        recommended: "POSSIBLY",
        demandLevel: "Medium",
        demandAmount: "$1,000 - $5,000",
        responseDays: 15,
        strategy:
          "Continue documenting validation and ownership issues before sending a demand letter.",
        leverage: [
          "Possible FCRA or FDCPA violations",
          "Validation concerns",
          "Incomplete investigation risk",
        ],
      };
    }

    return {
      recommended: "NO",
      demandLevel: "Low",
      demandAmount: "Not recommended yet",
      responseDays: 30,
      strategy:
        "Complete dispute rounds and collect more evidence before considering an attorney demand.",
      leverage: [
        "Developing file",
        "More documentation needed",
      ],
    };
  }

  function getStatuteLimitations(account) {
    const type = String(account.account_type || "").toLowerCase();
    const accountType = account.account_type || "Unknown";
    const state = "California";

    const writtenContractYears = 4;

    if (type.includes("charge")) {
      return {
        state,
        accountType,
        status: "Review Required",
        years: writtenContractYears,
        risk: "Medium",
        recommendation:
          "Determine Date of First Delinquency. Do not restart the statute by acknowledging the debt.",
      };
    }

    if (type.includes("collection")) {
      return {
        state,
        accountType,
        status: "Possible Time-Barred Debt",
        years: writtenContractYears,
        risk: "Low",
        recommendation:
          "Review last payment date and statute expiration before communicating with collector.",
      };
    }

    if (type.includes("inquiry")) {
      return {
        state,
        accountType,
        status: "Not Applicable",
        years: 0,
        risk: "Low",
        recommendation:
          "Statute of limitations generally does not apply to inquiries.",
      };
    }

    return {
      state,
      accountType,
      status: "Unknown",
      years: writtenContractYears,
      risk: "Low",
      recommendation:
        "Manual review recommended.",
    };
  }

  function getIdentityTheftAnalysis(account) {
    const type = String(account.account_type || "").toLowerCase();
    const status = String(account.status || "").toLowerCase();

    if (
      type.includes("inquiry") ||
      status.includes("fraud") ||
      status.includes("unauthorized")
    ) {
      return {
        risk: "Medium",
        recommended: "YES",
        nextStep: "Request proof of permissible purpose and consumer authorization.",
        laws: ["FCRA 604", "FCRA 605B", "FCRA 611"],
        documents: [
          "FTC Identity Theft Report if fraud is confirmed",
          "Police report if available",
          "Proof of identity",
          "Unauthorized inquiry statement",
        ],
      };
    }

    return {
      risk: "Low",
      recommended: "NO",
      nextStep: "Identity theft workflow is not recommended unless fraud or unauthorized activity is confirmed.",
      laws: ["FCRA 611"],
      documents: [
        "Continue standard dispute documentation",
      ],
    };
  }

  function getFtcAffidavitEngine(identityTheft) {

    if (identityTheft.risk === "High") {
      return {
        needed: "YES",
        strength: "High",
        laws: [
          "FCRA 605B",
          "FCRA 611"
        ],
        documents: [
          "FTC Identity Theft Report",
          "Police Report",
          "Driver's License",
          "Proof of Address",
          "Social Security Card"
        ],
        nextStep:
          "Submit FTC affidavit and request identity theft blocking procedures."
      };
    }

    if (identityTheft.risk === "Medium") {
      return {
        needed: "POSSIBLY",
        strength: "Medium",
        laws: [
          "FCRA 605B"
        ],
        documents: [
          "FTC Identity Theft Report",
          "Driver's License",
          "Proof of Address"
        ],
        nextStep:
          "Gather supporting documents before filing an FTC affidavit."
      };
    }

    return {
      needed: "NO",
      strength: "Low",
      laws: [
        "FCRA 611"
      ],
      documents: [
        "Continue standard dispute documentation"
      ],
      nextStep:
        "FTC affidavit not recommended unless fraud or identity theft is confirmed."
    };
  }

  function getCfpbEscalationEngine(
    account,
    fcra,
    litigation,
    evidence
  ) {
    if (
      litigation.risk === "High" &&
      evidence.caseReady
    ) {
      return {
        urgency: "High",
        complaintType: "Incorrect information on credit report",
        attachments: [
          "Credit reports",
          "Dispute letters",
          "Certified mail receipts",
          "Bureau responses",
          "Furnisher responses",
        ],
        strategy:
          "Prepare CFPB complaint after documenting investigation failures.",
      };
    }

    if (fcra.confidence >= 80) {
      return {
        urgency: "Medium",
        complaintType: "Failure to conduct reasonable investigation",
        attachments: [
          "Credit reports",
          "Dispute letters",
          "Bureau responses",
        ],
        strategy:
          "Continue dispute rounds and escalate if inaccurate reporting continues.",
      };
    }

    return {
      urgency: "Low",
      complaintType: "Not recommended yet",
      attachments: ["Continue documentation"],
      strategy: "Complete dispute rounds before CFPB escalation.",
    };
  }

  function getFurnisherDisputeEngine(
    account,
    fcra,
    litigation,
    evidence
  ) {
    const type = String(account.account_type || "").toLowerCase();
    const creditor = String(account.creditor || "").toLowerCase();

    if (
      litigation.risk === "High" ||
      fcra.confidence >= 85
    ) {
      return {
        recommended: "YES",
        target:
          account.creditor || "Furnisher",
        laws: [
          "FCRA 623(a)(1) - inaccurate furnishing",
          "FCRA 623(a)(8) - direct dispute duties",
          "FCRA 623(b) - investigation after bureau notice",
        ],
        evidence: [
          "Credit report showing inaccurate reporting",
          "Prior bureau dispute letters",
          "Bureau investigation results",
          "Payment history or account records",
        ],
        strategy:
          "Send a direct furnisher dispute demanding investigation, correction, deletion, and supporting documentation.",
      };
    }

    if (
      type.includes("collection") ||
      creditor.includes("midland") ||
      creditor.includes("portfolio") ||
      creditor.includes("lvnv") ||
      creditor.includes("jefferson")
    ) {
      return {
        recommended: "POSSIBLY",
        target:
          account.creditor || "Collector / Furnisher",
        laws: [
          "FCRA 623(a)(8) - direct dispute duties",
          "FDCPA 1692g - debt validation",
        ],
        evidence: [
          "Collection notice",
          "Credit report tradeline",
          "Debt validation request",
          "Ownership or assignment records",
        ],
        strategy:
          "Request validation, ownership chain, authority to report, and full account-level documentation.",
      };
    }

    return {
      recommended: "NO",
      target:
        account.creditor || "Furnisher",
      laws: [
        "FCRA 611 - continue bureau dispute process",
      ],
      evidence: [
        "Continue collecting bureau responses",
      ],
      strategy:
        "Direct furnisher dispute is not recommended yet. Complete bureau dispute rounds first.",
    };
  } 

  function getMetro2ComplianceEngine(account, fcra, furnisherDispute) {
    const type = String(account.account_type || "").toLowerCase();
    const status = String(account.status || "").toLowerCase();

    const violations = [];

    if (type.includes("charge")) {
      violations.push(
        "Verify charge-off date accuracy",
        "Check balance after charge-off",
        "Review past-due amount reporting",
        "Confirm account status is not reporting inconsistently"
      );
    }

    if (type.includes("collection")) {
      violations.push(
        "Verify collection open date",
        "Check original creditor reporting",
        "Review balance consistency",
        "Confirm ownership and assignment accuracy"
      );
    }

    if (type.includes("inquiry")) {
      violations.push(
        "Verify inquiry date accuracy",
        "Confirm permissible purpose coding",
        "Check consumer authorization records"
      );
    }

    if (
      status.includes("high") ||
      fcra.confidence >= 85 ||
      furnisherDispute.recommended === "YES"
    ) {
      violations.push(
        "High probability of furnishing accuracy issue",
        "Potential FCRA 623 reporting compliance concern"
      );
    }

    return {
      strength:
        violations.length >= 5
          ? "High"
          : violations.length >= 3
          ? "Medium"
          : "Low",
      violations,
      laws: [
        "Metro 2 reporting accuracy",
        "FCRA 607(b) - reasonable procedures",
        "FCRA 611 - reinvestigation",
        "FCRA 623 - furnisher accuracy duties",
      ],
      recommendation:
        "Use Metro 2 accuracy issues to strengthen bureau and furnisher disputes.",
    };
  }

  function getReAgingDetectionEngine(account, statute, metro2Compliance) {
    const type = String(account.account_type || "").toLowerCase();

    if (
      type.includes("charge") ||
      type.includes("collection")
    ) {
      return {
        risk:
          metro2Compliance.strength === "High"
            ? "High"
            : "Medium",
        suspected: "Review Required",
        redFlags: [
          "Compare Date of First Delinquency to date opened",
          "Check if collection date appears newer than original delinquency",
          "Review whether reporting period was restarted",
          "Confirm obsolete debt is not still reporting",
        ],
        laws: [
          "FCRA 605 - obsolete reporting",
          "FCRA 611 - reinvestigation",
          "FCRA 623 - furnisher accuracy duties",
          "Metro 2 date reporting accuracy",
        ],
        recommendation:
          "Review DOFD, date opened, last payment date, and reporting history for possible re-aging.",
      };
    }

    if (type.includes("inquiry")) {
      return {
        risk: "Low",
        suspected: "Not Applicable",
        redFlags: [
          "Re-aging generally does not apply to inquiries",
        ],
        laws: [
          "FCRA 604 - permissible purpose",
        ],
        recommendation:
          "Focus on permissible purpose and authorization instead of re-aging.",
      };
    }

    return {
      risk: "Low",
      suspected: "Unknown",
      redFlags: [
        "Manual date review recommended",
      ],
      laws: [
        "FCRA 611 - reinvestigation",
      ],
      recommendation:
        "Collect account date fields before determining re-aging risk.",
    };
  }

  function getZombieDebtEngine(account, statute) {
    const type = String(account.account_type || "").toLowerCase();

    if (
      type.includes("collection") ||
      type.includes("charge")
    ) {
      if (statute.years >= 4) {
        return {
          risk: "High",
          status: "Possible Zombie Debt",
          laws: [
            "FCRA 605",
            "FDCPA 807",
            "FDCPA 808"
          ],
          redFlags: [
            "Debt may be time-barred",
            "Debt buyer ownership should be verified",
            "Review for duplicate reporting",
            "Check for obsolete reporting"
          ],
          recommendation:
            "Investigate ownership and age of debt before responding or paying."
        };
      }

      return {
        risk: "Medium",
        status: "Monitor",
        laws: [
          "FDCPA validation rights"
        ],
        redFlags: [
          "Verify ownership",
          "Request validation"
        ],
        recommendation:
          "Continue documentation and monitor collection activity."
      };
    }

    return {
      risk: "Low",
      status: "Not Applicable",
      laws: [
      "FCRA 611"
      ],
      redFlags: [
        "No zombie debt indicators"
      ],
      recommendation:
        "Continue normal dispute workflow."
    };
  }

  function getDuplicateAccountEngine(account) {
    const type = String(account.account_type || "").toLowerCase();

    if (
      type.includes("collection") ||
      type.includes("charge")
    ) {
      return {
        risk: "Medium",
        status: "Review Required",
        laws: [
          "FCRA 611",
          "FCRA 623"
        ],
        redFlags: [
          "Original creditor and collection account may both be reporting",
          "Duplicate balances may inflate utilization",
          "Review ownership history",
          "Check for duplicate tradelines"
        ],
        recommendation:
          "Investigate whether multiple entities are reporting the same debt."
      };
    }

    return {
      risk: "Low",
      status: "No duplicate indicators",
      laws: [
        "FCRA 611"
      ],
      redFlags: [
        "No duplicate reporting detected"
      ],
      recommendation:
        "Continue standard dispute workflow."
    };
  }

  function getBankruptcyViolationEngine(account) {
    const status = String(account.status || "").toLowerCase();
    const remarks = String(account.remarks || "").toLowerCase();
    const balance = Number(account.balance || 0);

    if (
      remarks.includes("bankruptcy") ||
      status.includes("discharged")
    ) {
      if (balance > 0) {
        return {
          risk: "High",
          status: "Possible Violation",
          violations: [
            "Balance may still be reporting after bankruptcy",
            "Account status should be reviewed",
            "Late payments after discharge should be investigated",
            "Charge-off reporting should be verified",
          ],
          laws: [
            "FCRA 623",
            "FCRA 611",
            "Bankruptcy discharge protections",
          ],
          recommendation:
            "Review discharge records and challenge inaccurate post-bankruptcy reporting.",
        };
      }

      return {
        risk: "Medium",
        status: "Review Required",
        violations: [
          "Verify account status after discharge",
          "Review payment history",
        ],
        laws: [
          "FCRA 623",
          "FCRA 611",
        ],
      recommendation:
        "Confirm account is reporting accurately following bankruptcy discharge.",
      };
    }

    return {
      risk: "Low",
      status: "No Bankruptcy Issues",
      violations: [
        "No bankruptcy-related reporting concerns detected",
      ],
      laws: [
        "FCRA 611",
      ],
      recommendation:
        "Continue monitoring account reporting.",
    };
  }

  function getChargeOffAccuracyEngine(account) {
    const type = String(account.account_type || "").toLowerCase();
    const balance = Number(account.balance || 0);

    if (type.includes("charge")) {
      return {
        risk: balance > 0 ? "High" : "Medium",
        status: "Review Required",
        violations: [
          "Verify charge-off balance accuracy",
          "Review past due amount",
          "Confirm charge-off date",
          "Check for improper monthly updating",
        ],
        laws: [
          "FCRA 623",
          "FCRA 611",
          "Metro 2 reporting standards",
        ],
        recommendation:
          "Challenge any inaccurate balances, dates, or payment history after charge-off.",
      };
    }

    return {
      risk: "Low",
      status: "Not Applicable",
      violations: [
        "No charge-off indicators detected",
      ],
      laws: [
        "FCRA 611",
      ],
      recommendation:
        "Continue normal dispute workflow.",
    };
  }

  function getCollectionAuthorityEngine(account) {
    const type = String(account.account_type || "").toLowerCase();
    const creditor = String(account.creditor || "").toLowerCase();

    if (
      type.includes("collection") ||
      creditor.includes("midland") ||
      creditor.includes("portfolio") ||
      creditor.includes("lvnv") ||
      creditor.includes("jefferson")
    ) {
      return {
        risk: "High",
        status: "Authority Review Required",
        violations: [
          "Verify collector has legal authority to report",
          "Request full chain of assignment",
          "Confirm original creditor ownership transfer",
          "Review debt validation documentation",
        ],
        laws: [
          "FDCPA 809 - validation of debts",
          "FDCPA 808 - unfair practices",
          "FCRA 623 - furnisher accuracy duties",
          "FCRA 611 - reinvestigation",
        ],
        recommendation:
          "Challenge collection authority and demand complete ownership, assignment, and validation records.",
      };
    }

    return {
      risk: "Low",
      status: "Not Applicable",
      violations: [
        "No collection authority concerns detected",
      ],
      laws: [
        "FCRA 611",
      ],
      recommendation:
        "Continue standard dispute workflow.",
    };
  }

  function getMixedFileEngine(account) {
    const type = String(account.account_type || "").toLowerCase();
    const creditor = String(account.creditor || "").toLowerCase();
    const remarks = String(account.remarks || "").toLowerCase();

    if (
      remarks.includes("identity") ||
      remarks.includes("mixed") ||
      remarks.includes("fraud") ||
      creditor.includes("medical") ||
      type.includes("authorized")
    ) {
      return {
        risk: "High",
        status: "Contamination Review Required",
        violations: [
          "Possible mixed file or identity contamination",
          "Verify account belongs to consumer",
          "Review aliases, addresses, and personal identifiers",
          "Check for unauthorized or merged tradelines",
        ],
        laws: [
          "FCRA 605B - identity theft blocking",
          "FCRA 611 - reinvestigation",
          "FCRA 623 - furnisher accuracy duties",
        ],
        recommendation:
          "Dispute as possible mixed file or identity contamination and request full verification of ownership and identifiers.",
      };
    }

    return {
      risk: "Low",
      status: "No Mixed File Indicators",
      violations: [
        "No mixed file or contamination indicators detected",
      ],
      laws: [
        "FCRA 611",
      ],
      recommendation:
        "Continue standard dispute workflow.",
    };
  }

  function getSecondaryBureauEngine(account) {
    const type = String(account.account_type || "").toLowerCase();

    if (
      type.includes("collection") ||
      type.includes("charge")
    ) {
      return {
        risk: "Medium",
        status: "Secondary Bureau Review Recommended",
        violations: [
          "LexisNexis personal information should be verified",
          "Innovis data should be reviewed",
          "CoreLogic records may affect reporting",
          "Employment and address history should be checked",
        ],
        laws: [
          "FCRA 611",
          "FCRA 623",
          "FCRA 607(b)"
        ],
        recommendation:
          "Review secondary reporting agencies and dispute inaccurate identifying information.",
      };
    }

    return {
      risk: "Low",
      status: "No Secondary Bureau Issues",
      violations: [
        "No secondary bureau indicators detected",
      ],
      laws: [
        "FCRA 611"
      ],
      recommendation:
        "Continue standard dispute workflow.",
    };
  }

  function getInquiryPermissiblePurposeEngine(account) {
    const type = String(account.account_type || "").toLowerCase();

    if (type.includes("inquiry")) {
      return {
        risk: "High",
        status: "Permissible Purpose Verification Required",
        violations: [
          "Creditor must have permissible purpose",
          "Consumer authorization should be verified",
          "Inquiry date should be reviewed",
          "Business relationship should be documented",
        ],
        laws: [
          "FCRA 604",
          "FCRA 611",
        ],
        recommendation:
          "Request proof of permissible purpose and authorization for the inquiry.",
      };
    }

    return {
      risk: "Low",
      status: "No Inquiry Violations Detected",
      violations: [
        "No unauthorized inquiry indicators detected",
      ],
      laws: [
        "FCRA 611",
      ],
      recommendation:
        "Continue standard dispute workflow.",
    };
  }

  function getDofdViolationEngine(account) {
    const type = String(account.account_type || "").toLowerCase();
    const status = String(account.status || "").toLowerCase();
    const remarks = String(account.remarks || "").toLowerCase();

    if (
      type.includes("charge") ||
      type.includes("collection") ||
      status.includes("charge") ||
      remarks.includes("delinquency")
    ) {
      return {
        risk: "High",
        status: "DOFD Review Required",
        violations: [
          "Date of First Delinquency should be verified",
          "Check for re-aging or restarted reporting period",
          "Compare DOFD against charge-off date",
          "Confirm obsolete reporting period has not been extended",
        ],
        laws: [
          "FCRA 605 - obsolete reporting",
          "FCRA 611 - reinvestigation",
          "FCRA 623 - furnisher accuracy duties",
          "Metro 2 DOFD reporting accuracy",
        ],
        recommendation:
          "Request verification of Date of First Delinquency and challenge any inaccurate or re-aged reporting.",
      };
    }

    return {
      risk: "Low",
      status: "No DOFD Indicators",
      violations: [
        "No DOFD-related concerns detected",
      ],
      laws: [
        "FCRA 611",
      ],
      recommendation:
        "Continue standard dispute workflow.",
    };
  }

  function getPaymentHistoryConsistencyEngine(account) {
    const type = String(account.account_type || "").toLowerCase();
    const status = String(account.status || "").toLowerCase();

    if (
      type.includes("charge") ||
      type.includes("collection") ||
      status.includes("late")
    ) {
      return {
        risk: "High",
        status: "Payment History Review Required",
        violations: [
          "Late payment history should match account status",
          "Payment history should not conflict with balance",
          "Charge-off dates should align with reporting history",
          "Monthly reporting pattern should be consistent",
        ],
        laws: [
          "FCRA 611",
          "FCRA 623",
          "Metro 2 payment history requirements",
        ],
        recommendation:
          "Review monthly payment history for inaccuracies or contradictions and dispute inconsistent reporting.",
      };
    }

    return {
      risk: "Low",
      status: "No Payment History Issues",
      violations: [
        "No payment history inconsistencies detected",
      ],
      laws: [
        "FCRA 611",
      ],
      recommendation:
        "Continue standard dispute workflow.",
    };
  }

  function getUtilizationManipulationEngine(account) {
    const type = String(account.account_type || "").toLowerCase();
    const balance = Number(account.balance || 0);
    const creditLimit = Number(
      account.credit_limit ||
      account.limit ||
      account.high_credit ||
      0
    );

    if (
      type.includes("credit") ||
      type.includes("card") ||
      creditLimit > 0
    ) {
      const utilization =
        creditLimit > 0
          ? Math.round((balance / creditLimit) * 100)
          : 0;

      return {
        risk: utilization >= 90 ? "High" : utilization >= 50 ? "Medium" : "Low",
        status: "Utilization Review Required",
        utilization,
        violations: [
          "Verify balance reporting accuracy",
          "Confirm credit limit or high credit is accurate",
          "Review utilization impact on score",
          "Check for balance-to-limit inconsistencies",
        ],
        laws: [
          "FCRA 611",
          "FCRA 623",
          "Metro 2 balance and credit limit accuracy",
        ],
        recommendation:
          "Dispute inaccurate balances, limits, or high-credit fields that may inflate utilization.",
      };
    }

    return {
      risk: "Low",
      status: "Not Applicable",
      utilization: 0,
      violations: [
        "No revolving utilization indicators detected",
      ],
      laws: [
        "FCRA 611",
      ],
      recommendation:
        "Continue standard dispute workflow.",
    };
  }

  function getCreditorNameConsistencyEngine(account) {
    const creditor = String(
      account.creditor ||
      account.creditor_name ||
      account.original_creditor ||
      ""
    ).toLowerCase();

    if (
      creditor.includes("llc") ||
      creditor.includes("portfolio") ||
      creditor.includes("recovery") ||
      creditor.includes("funding") ||
      creditor.includes("capital")
    ) {
      return {
        risk: "High",
        status: "Ownership Verification Required",
        violations: [
          "Original creditor and current creditor should be verified",
          "Ownership chain may require documentation",
          "Creditor naming consistency should be reviewed",
          "Assignment records should be confirmed",
        ],
        laws: [
          "FCRA 611",
          "FCRA 623",
          "Metro 2 ownership reporting standards",
        ],
        recommendation:
          "Request documentation proving ownership and verify that all creditor names are reported consistently.",
      };
    }

    return {
      risk: "Low",
      status: "No Creditor Name Issues",
      violations: [
        "No creditor naming inconsistencies detected",
      ],
      laws: [
        "FCRA 611",
      ],
      recommendation:
        "Continue standard dispute workflow.",
    };
  }

  function getAccountStatusContradictionEngine(account) {
    const type = String(account.account_type || "").toLowerCase();
    const status = String(account.status || "").toLowerCase();
    const balance = Number(account.balance || 0);

    if (
      (status.includes("charged off") && status.includes("current")) ||
      (status.includes("closed") && status.includes("late")) ||
      (type.includes("collection") &&
        status.includes("current")) ||
      (type.includes("collection") &&
        balance > 0 &&
        status.includes("paid"))
    ) {
      return {
        risk: "High",
        status: "Contradictory Reporting Detected",
        violations: [
          "Account status appears inconsistent",
          "Metro 2 reporting should be reviewed",
          "Balance and status may conflict",
          "Payment history and account condition should align",
        ],
        laws: [
          "FCRA 611",
          "FCRA 623",
          "Metro 2 account status standards",
        ],
        recommendation:
          "Dispute contradictory account statuses and request verification of reporting accuracy.",
       };
    }

    return {
      risk: "Low",
      status: "No Status Contradictions",
      violations: [
        "No contradictory account status indicators detected",
      ],
      laws: [
        "FCRA 611",
      ],
      recommendation:
        "Continue standard dispute workflow.",
    };
  }

  function getBalanceAccuracyEngine(account) {
    const type = String(account.account_type || "").toLowerCase();
    const status = String(account.status || "").toLowerCase();
    const balance = Number(account.balance || 0);

    if (
      balance > 0 ||
      type.includes("collection") ||
      type.includes("charge")
    ) {
      return {
        risk:
          status.includes("paid") && balance > 0
            ? "High"
            : balance > 0
            ? "Medium"
            : "Low",
        status: "Balance Review Required",
        violations: [
          "Verify current balance accuracy",
          "Check whether paid or closed accounts still report a balance",
          "Review collection or charge-off balance calculations",
          "Confirm balance matches account status and payment history",
        ],
        laws: [
          "FCRA 611",
          "FCRA 623",
          "Metro 2 balance reporting standards",
        ],
        recommendation:
          "Dispute inaccurate, inflated, or inconsistent balances and request full balance verification.",
      };
    }

    return {
      risk: "Low",
      status: "No Balance Issues",
      violations: [
        "No balance accuracy concerns detected",
      ],
      laws: [
      "FCRA 611",
    ],
    recommendation:
      "Continue standard dispute workflow.",
   };
 }

  function getDateLastReportedEngine(account) {
    const type = String(account.account_type || "").toLowerCase();
    const status = String(account.status || "").toLowerCase();
    const lastReported = String(
      account.date_last_reported ||
      account.last_reported ||
      ""
    ).toLowerCase();

    if (
      type.includes("collection") ||
      type.includes("charge") ||
      status.includes("collection") ||
      lastReported !== ""
    ) {
      return {
        risk: "High",
        status: "Date Last Reported Review Required",
        violations: [
          "Verify Date Last Reported accuracy",
          "Check for monthly refreshing without account activity",
          "Review for re-aging attempts",
          "Confirm reporting period has not been artificially extended",
        ],
        laws: [
          "FCRA 605",
          "FCRA 611",
          "FCRA 623",
          "Metro 2 date reporting standards",
        ],
        recommendation:
          "Challenge suspicious Date Last Reported updates and request verification of actual account activity.",
      };
    }

    return {
      risk: "Low",
      status: "No Date Last Reported Issues",
      violations: [
        "No suspicious reporting activity detected",
      ],
      laws: [
        "FCRA 611",
      ],
      recommendation:
        "Continue standard dispute workflow.", 
    };
  }

  function getHighUtilizationRiskEngine(account) {
    const type = String(account.account_type || "").toLowerCase();
    const balance = Number(account.balance || 0);
    const creditLimit = Number(
      account.credit_limit ||
      account.limit ||
      account.high_credit ||
      0
    );

    if (
      type.includes("credit") ||
      type.includes("card") ||
      creditLimit > 0
    ) {
      const utilization =
        creditLimit > 0
          ? Math.round((balance / creditLimit) * 100)
          : 0;

      if (utilization >= 75) {
        return {
          risk: "High",
          status: "High Utilization Detected",
          utilization,
          violations: [
            "Account may be suppressing credit score due to high utilization",
            "Balance-to-limit ratio should be verified",
            "Credit limit accuracy should be confirmed",
            "High utilization may indicate inaccurate balance reporting",
          ],
          laws: [
            "FCRA 611",
            "FCRA 623",
            "Metro 2 revolving utilization standards",
          ],
          recommendation:
            "Review balance and credit limit accuracy and dispute any reporting that inflates utilization.",
        };
      }

      if (utilization >= 50) {
        return {
          risk: "Medium",
          status: "Elevated Utilization",
          utilization,
          violations: [
            "Utilization is above optimal scoring range",
            "Balance and limit should be reviewed for accuracy",
            "Credit limit reporting should be verified",
          ],
          laws: [
            "FCRA 611",
            "FCRA 623",
          ],
          recommendation:
            "Verify balance and credit limit reporting and consider utilization reduction strategy.",
        };
      }

      return {
        risk: "Low",
        status: "Utilization Acceptable",
        utilization,
        violations: [
          "No high utilization risk detected",
        ],
        laws: [
          "FCRA 611",
        ],
        recommendation:
          "Continue standard dispute workflow.",
      };
    }

    return {
      risk: "Low",
      status: "Not Applicable",
      utilization: 0,
      violations: [
        "No revolving account utilization indicators detected",
      ],
      laws: [
        "FCRA 611",
      ],
      recommendation:
        "Continue standard dispute workflow.",
    };
  }

  function getAccountAgeManipulationEngine(account) {
    const type = String(account.account_type || "").toLowerCase();
    const status = String(account.status || "").toLowerCase();
    const dateOpened = String(
      account.date_opened ||
      account.opened_date ||
      account.open_date ||
      ""
    ).toLowerCase();

    if (
      type.includes("collection") ||
      type.includes("charge") ||
      status.includes("collection") ||
      dateOpened !== ""
    ) {
      return {
        risk: "High",
        status: "Account Age Review Required",
        violations: [
          "Verify account open date accuracy",
          "Check whether sold debt is reporting as newly opened",
          "Review for artificial account aging or re-aging",
          "Compare account age against DOFD and Date Last Reported",
        ],
        laws: [
          "FCRA 605",
          "FCRA 611",
          "FCRA 623",
          "Metro 2 account age reporting standards",
        ],
        recommendation:
          "Challenge suspicious account age reporting and request verification of original account opening and delinquency dates.",
      };
    }

    return {
      risk: "Low",
      status: "No Account Age Issues",
      violations: [
        "No account age manipulation indicators detected",
      ],
      laws: [
      "FCRA 611",
      ],
      recommendation:
        "Continue standard dispute workflow.",
    };
  }

  function getCreditLimitAccuracyEngine(account) {
    const type = String(account.account_type || "").toLowerCase();

    const balance = Number(account.balance || 0);

    const creditLimit = Number(
      account.credit_limit ||
      account.limit ||
      account.high_credit ||
      0
    );

    if (
      type.includes("credit") ||
      type.includes("card") ||
      creditLimit > 0
    ) {
      return {
        risk:
          creditLimit === 0
            ? "High"
            : balance > creditLimit
            ? "High"
            : "Medium",

        status: "Credit Limit Review Required",

        violations: [
          "Verify credit limit accuracy",
          "Review high-credit amount",
          "Confirm balance does not exceed reported limit",
          "Check for missing or suppressed credit limits",
        ],
 
        laws: [
          "FCRA 611",
          "FCRA 623",
          "Metro 2 revolving account standards",
        ],

        recommendation:
          "Challenge inaccurate or missing credit limit reporting and request verification of the original limit.",
      };
    }

    return {
      risk: "Low",

      status: "No Credit Limit Issues",

      violations: [
        "No credit limit inconsistencies detected",
      ],

      laws: [
        "FCRA 611",
      ],

      recommendation:
        "Continue standard dispute workflow.",
    };
  }

  function getMonthlyPaymentAccuracyEngine(account) {
    const type = String(account.account_type || "").toLowerCase();

    const monthlyPayment = Number(
      account.monthly_payment ||
      account.payment_amount ||
      0
    );

    if (
      type.includes("collection") ||
      type.includes("charge") ||
      monthlyPayment > 0
    ) {
      return {
        risk: "Medium",

        status: "Monthly Payment Review Required",

        violations: [
          "Verify monthly payment amount accuracy",
          "Collections should not report active monthly obligations",
          "Charge-offs should be reviewed for improper payment reporting",
          "Payment amount should match account status",
        ],

        laws: [
          "FCRA 611",
          "FCRA 623",
          "Metro 2 payment amount standards",
        ],

        recommendation:
          "Challenge inaccurate payment amounts and request verification of the actual payment obligation.",
      };
    }

    return {
      risk: "Low",

      status: "No Monthly Payment Issues",

      violations: [
        "No monthly payment inconsistencies detected",
      ],

      laws: [
        "FCRA 611",
      ],

      recommendation:
        "Continue standard dispute workflow.",
    };
  }

  function getPastDueAmountAccuracyEngine(account) {
    const type = String(account.account_type || "").toLowerCase();
    const status = String(account.status || "").toLowerCase();

    const balance = Number(account.balance || 0);

    const pastDue = Number(
      account.past_due ||
      account.past_due_amount ||
      account.amount_past_due ||
      0
    );

    if (
      pastDue > 0 ||
      type.includes("collection") ||
      type.includes("charge") ||
      status.includes("late")
    ) {
      return {
        risk:
          balance === 0 && pastDue > 0
            ? "High"
            : pastDue > 0
            ? "Medium"
            : "Low",

        status: "Past Due Amount Review Required",

        violations: [
          "Verify past due amount accuracy",
          "Check whether paid or zero-balance accounts still report past due amounts",
          "Review charge-offs for improper current delinquency reporting",
          "Confirm past due amount matches account status and payment history",
        ],

        laws: [
          "FCRA 611",
          "FCRA 623",
          "Metro 2 delinquency reporting standards",
        ],

        recommendation:
          "Dispute inaccurate past due amounts and request verification of the delinquency calculation.",
      };
    }

    return {
      risk: "Low",

      status: "No Past Due Amount Issues",

      violations: [
        "No past due amount inconsistencies detected",
      ],
 
      laws: [
        "FCRA 611",
      ],

      recommendation:
         "Continue standard dispute workflow.",
     };
  }

  function getHighCreditAmountAccuracyEngine(account) {
    const balance = Number(account.balance || 0);

    const highCredit = Number(
      account.high_credit ||
      account.credit_limit ||
      account.limit ||
      0
    );

    if (highCredit > 0 || balance > 0) {
      return {
        risk:
          balance > highCredit && highCredit > 0
            ? "High"
            : "Medium",

        status: "High Credit Review Required",

        violations: [
          "Verify high-credit amount accuracy",
          "Review whether balance exceeds high credit",
          "Check for missing or suppressed limits",
          "Confirm consistency with Metro 2 reporting",
        ],

        laws: [
          "FCRA 611",
          "FCRA 623",
          "Metro 2 revolving account standards",
        ],

        recommendation:
          "Dispute inaccurate high-credit reporting and request verification of the original limit.",
      };
    }

    return {
      risk: "Low",

      status: "No High Credit Issues",

      violations: [
        "No high-credit inconsistencies detected",
      ],

      laws: [
        "FCRA 611",
      ],

      recommendation:
        "Continue standard dispute workflow.",
    };
  }

  function getMasterRecommendationEngine(account, engines) {
    const highRiskEngines = engines.filter(
      (engine) => engine?.risk === "High"
    );

    const mediumRiskEngines = engines.filter(
      (engine) => engine?.risk === "Medium"
    );

    const priority =
      highRiskEngines.length > 0
        ? "High"
        : mediumRiskEngines.length > 0
        ? "Medium"
        : "Low";

    const primaryEngine =
      highRiskEngines[0] ||
      mediumRiskEngines[0] ||
      engines[0];

    const primaryViolation =
      primaryEngine?.title ||
      primaryEngine?.status ||
      primaryEngine?.riskReason ||
      "General Accuracy Review";

    const bestLetter =
      primaryViolation.includes("Inquiry")
        ? "FCRA 604 Inquiry Dispute"
        : primaryViolation.includes("Bankruptcy")
        ? "Bankruptcy Reporting Dispute"
        : primaryViolation.includes("Collection")
        ? "Collection Authority / Debt Validation Letter"
        : primaryViolation.includes("Balance")
        ? "Metro 2 Balance Accuracy Dispute"
        : primaryViolation.includes("DOFD")
        ? "DOFD / Re-Aging Dispute"
        : "General FCRA 611 Accuracy Dispute";

    const cfpbReady =
      highRiskEngines.length >= 2 ? "YES" : "NO";

    const attorneyDemandReady =
      highRiskEngines.length >= 3 ? "POSSIBLY" : "NO";

    const recommendation =
      priority === "High"
        ? "This account shows high-risk reporting issues. Start with the strongest dispute letter, document all responses, and prepare for escalation if the account is verified without proper evidence."
        : priority === "Medium"
        ? "This account has moderate reporting concerns. Begin with a targeted dispute and collect supporting documentation before escalation."
        : "This account does not show major red flags yet. Continue monitoring and use a standard accuracy dispute if needed.";

    return {
      priority,
      primaryViolation,
      bestLetter,
      cfpbReady,
      attorneyDemandReady,
      recommendation,
    };
  }

  function getEvidenceChecklistEngine(account, primaryViolation) {
    if (primaryViolation.includes("Collection")) {
      return {
        evidence: [
          "Current Credit Report",
          "Collection Letter",
          "Debt Validation Response",
          "Previous Bureau Response",
        ],
      };
    }

    if (primaryViolation.includes("Bankruptcy")) {
      return {
        evidence: [
          "Current Credit Report",
          "Bankruptcy Discharge",
          "Previous Bureau Response",
        ],
      };
    }

    if (primaryViolation.includes("Inquiry")) {
      return {
        evidence: [
          "Current Credit Report",
          "Creditor Authorization",
          "Business Relationship Documentation",
        ],
      };
    }

    return {
      evidence: [
        "Current Credit Report",
        "Previous Credit Report",
        "Bureau Response",
      ],
    };
  }

  function getSmartLetterRoutingEngine(account, masterRecommendation) {
    const protectAccount =
      account?.protect_account === true ||
      account?.protect_account === 1 ||
      account?.protect_account === "true";

    if (protectAccount) {
      return {
        letterType: "No Dispute Recommended",
        reason:
          "This is a protected positive tradeline. Leave it undisputed unless the consumer identifies specific inaccurate information.",
      };
    }

    const violation = String(
      masterRecommendation?.primaryViolation || ""
    ).toLowerCase();

    const accountText = [
      account?.creditor,
      account?.creditor_name,
      account?.account_type,
      account?.status,
      account?.remarks,
      account?.suggested_action,
      account?.issues,
      account?.next_action,
      violation,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    if (
      accountText.includes("identity theft") ||
      accountText.includes("fraudulent") ||
      accountText.includes("not mine")
    ) {
      return {
        letterType: "Identity Theft Dispute",
        reason:
          "The account contains possible identity-theft or unauthorized-account indicators.",
      };
    }

    if (
      accountText.includes("bankruptcy") ||
      accountText.includes("discharge") ||
      accountText.includes("included in bankruptcy")
    ) {
      return {
        letterType: "Bankruptcy Accuracy Dispute",
        reason:
          "Bankruptcy status, discharge reporting, balance, payment history, and account status should be verified.",
      };
    }

    if (
      accountText.includes("collection") ||
      accountText.includes("debt collector")
    ) {
      return {
        letterType: "Collection Account Dispute",
        reason:
          "Collection ownership, authority, balance, dates, and account status should be verified.",
      };
    }

    if (
      accountText.includes("charge off") ||
      accountText.includes("charge-off") ||
      accountText.includes("charged off")
    ) {
      return {
        letterType: "Charge-Off Dispute",
        reason:
          "Charge-off status, balance, payment history, and reporting dates should be verified.",
      };
    }

    if (
      accountText.includes("late payment") ||
      accountText.includes("late payments") ||
      accountText.includes("delinquent") ||
      accountText.includes("30 days late") ||
      accountText.includes("60 days late") ||
      accountText.includes("90 days late")
    ) {
      return {
        letterType: "Late Payment Dispute",
        reason:
          "The reported payment history and each listed late payment should be verified.",
      };
    }

    if (
      accountText.includes("inquiry") ||
      accountText.includes("permissible purpose") ||
      accountText.includes("unauthorized inquiry")
    ) {
      return {
        letterType: "Inquiry Dispute",
        reason:
          "Authorization and permissible purpose for the inquiry should be verified.",
      };
    }

    if (
      accountText.includes("dofd") ||
      accountText.includes("re-aging") ||
      accountText.includes("reaging") ||
      accountText.includes("date of first delinquency")
    ) {
      return {
        letterType: "General Dispute",
        reason:
          "The date of first delinquency and reporting timeline should be verified for accuracy.",
      };
    }

    if (
      accountText.includes("balance") ||
      accountText.includes("metro 2")
    ) {
      return {
        letterType: "General Dispute",
        reason:
          "The reported balance, status, and Metro 2 data should be verified for accuracy.",
      };
    }

    return {
      letterType: "General Dispute",
      reason:
        "The account should be investigated for accurate, complete, and verifiable reporting.",
    };
  }

  function getCaseBuilderEngine(
    account,
    masterRecommendation,
    evidenceChecklist,
    smartLetterRouting
  ) {
    const priority = masterRecommendation.priority || "Low";

    const caseStatus =
      priority === "High"
        ? "Active - High Priority"
        : priority === "Medium"
        ? "Active - Review Needed"
        : "Monitoring";

    const nextStep =
      priority === "High"
        ? "Generate recommended dispute letter and collect missing evidence."
        : priority === "Medium"
        ? "Review account details and prepare targeted dispute."
        : "Monitor account and dispute only if inaccurate reporting is confirmed.";

    return {
      caseStatus,
      priority,
      recommendedLetter: smartLetterRouting.letterType,
      routingReason: smartLetterRouting.reason,
      evidenceNeeded: evidenceChecklist.evidence,
      escalation:
        masterRecommendation.cfpbReady === "YES"
          ? "CFPB Ready"
          : "Standard Dispute First",
      attorneyDemand:
        masterRecommendation.attorneyDemandReady,
      nextStep,
    };
  }

  function getCFPBPacketEngine(
    account,
    masterRecommendation,
    evidenceChecklist,
    caseBuilder
  ) {
    return {
      complaintSummary:
        masterRecommendation.recommendation,

      violations: [
        masterRecommendation.primaryViolation,
      ],

      evidence:
        evidenceChecklist.evidence,

      requestedResolution:
        caseBuilder.recommendedLetter,

      escalation:
        caseBuilder.escalation,
    };
 }

  async function handleGenerateAccountDispute(account) {
    const effectiveClientId =
      selectedClientId || account?.client_id;

    if (!effectiveClientId) {
      setError("Unable to identify the client for this account.");
      return;
    }

    try {
      setUploading(true);
      setError("");
      setSuccessMessage("");

      const client = clients.find(
        (item) => String(item.id) === String(effectiveClientId)
      );

      const formData = new FormData();

      const recommendedLetterType =
        account.recommended_letter ||
        account.recommendedLetter ||
        account.letter_type ||
        "General Dispute";

      formData.append("template", recommendedLetterType);
      formData.append("letter_type", recommendedLetterType);
      formData.append("client_id", String(effectiveClientId));
      formData.append("name", client?.full_name || client?.name || "Client");
      formData.append("bureau", account.bureau || bureau || "Experian");
      formData.append("creditor", account.bureau || bureau || "Experian");
      formData.append("account", account.account_number || "N/A");
      formData.append("account_number", account.account_number || "N/A");
      formData.append(
        "details",
        "Creditor / Furnisher: " +
          (account.creditor || "N/A") +
          "\nAccount Number: " +
          (account.account_number || "N/A") +
          "\nReason: " +
          (account.suggested_action ||
            "Please investigate this account for accuracy, completeness, and compliance.")
      );

   
   const createdLetter = await generateLetter(formData);

   if (!createdLetter?.id) {
     throw new Error("The letter was created without a valid letter ID.");
   }

   await updateCreditReportAccountLetter(
     account.id,
     createdLetter.id
   );

   setAccounts((currentAccounts) =>
     currentAccounts.map((item) =>
       String(item.id) === String(account.id)
         ? {
             ...item,
             generated_letter_id: createdLetter?.id,
           }
         : item
    )
  );

  setSelectedAccount((currentAccount) =>
    String(currentAccount?.id) === String(account.id)
      ? {
          ...currentAccount,
          generated_letter_id: createdLetter?.id,
        }
      : currentAccount
  );

  setSuccessMessage(
    "Dispute letter generated for " +
    (account.creditor || "account") +
    "."
  );
  } catch (err) {
    console.error("Generate letter failed:", err);
    setError(err?.message || "Failed to generate dispute letter");
  } finally {
    setUploading(false);
  }
}

  async function handleGenerateOpenedCaseLetter() {
    if (!openedCase) {
      setError("No saved case is currently open.");
      return;
    }

    const effectiveClientId =
      selectedClientId || openedCase.client_id;

    if (!effectiveClientId) {
      setError(
        "Unable to identify the client for this saved case."
      );
      return;
    }

    try {
      setUploading(true);
      setError("");
      setSuccessMessage("");

      const client = clients.find(
        (item) =>
          String(item.id) ===
          String(effectiveClientId)
      );

      const matchingAccount = accounts.find((item) => {
        const itemCreditor = String(
          item.creditor ||
          item.creditor_name ||
          item.account_name ||
          ""
        )
          .trim()
          .toLowerCase();

        const caseAccount = String(
          openedCase.account_name || ""
        )
          .trim()
          .toLowerCase();

        const itemBureau = String(
          item.bureau || ""
        )
          .trim()
          .toLowerCase();

        const caseBureau = String(
          openedCase.bureau || ""
        )
          .trim()
          .toLowerCase();

        return (
          itemCreditor === caseAccount &&
          (
            !caseBureau ||
            !itemBureau ||
            itemBureau === caseBureau
          )
        );
      });

      const recommendedLetterType =
        openedCase.recommended_letter ||
        openedCase.full_analysis?.caseBuilder?.recommendedLetter ||
        openedCase.full_analysis?.smartLetterRouting?.letterType ||
        "General Dispute";

      const creditorName =
        openedCase.account_name ||
        matchingAccount?.creditor ||
        "Unknown Creditor";

      const selectedBureau =
        openedCase.bureau ||
        matchingAccount?.bureau ||
        bureau ||
        "Experian";

      const accountNumber =
        matchingAccount?.account_number ||
        matchingAccount?.accountNumber ||
        "N/A";

      const disputeReason =
        openedCase.primary_violation ||
        openedCase.full_analysis?.smartLetterRouting?.reason ||
        openedCase.next_step ||
        "Please investigate this account for accuracy, completeness, and compliance.";

      const formData = new FormData();

      formData.append(
        "template",
        recommendedLetterType
      );

      formData.append(
        "letter_type",
        recommendedLetterType
      );

      formData.append(
        "client_id",
        String(effectiveClientId)
      );

      formData.append(
        "name",
        client?.full_name ||
        client?.name ||
        "Client"
      );

      formData.append(
        "bureau",
        selectedBureau
      );

      formData.append(
        "creditor",
        selectedBureau
      );

      formData.append(
        "creditor_name",
        creditorName
      );

      formData.append(
        "account",
        accountNumber
      );

      formData.append(
        "account_number",
        accountNumber
      );

      formData.append(
        "details",
        "Creditor / Furnisher: " +
          creditorName +
          "\nAccount Number: " +
          accountNumber +
          "\nReason: " +
          disputeReason +
          (
            openedCase.next_step
              ? "\nNext Step: " +
                openedCase.next_step
              : ""
          )
      );

      const createdLetter =
        await generateLetter(formData);

      if (!createdLetter?.id) {
        throw new Error(
          "The letter was created without a valid letter ID."
        );
      }

      /*
       * Link the generated letter back to the original
       * credit-report account only when we found a real
       * matching account. Never use the Case File ID here.
       */
      if (matchingAccount?.id) {
        await updateCreditReportAccountLetter(
          matchingAccount.id,
          createdLetter.id
        );

        setAccounts((currentAccounts) =>
          currentAccounts.map((item) =>
            String(item.id) ===
            String(matchingAccount.id)
              ? {
                  ...item,
                  generated_letter_id:
                    createdLetter.id,
                }
              : item
          )
        );
      }

      setSuccessMessage(
        recommendedLetterType +
          " generated successfully for " +
          creditorName +
          `. Letter ID: ${createdLetter.id}`
      );

      window.dispatchEvent(
        new Event(
          "integrity-dashboard-refresh"
        )
      );
    } catch (err) {
      console.error(
        "Generate restored case letter failed:",
        err
      );

      setError(
        err?.message ||
          "Failed to generate the saved case dispute letter."
      );
    } finally {
      setUploading(false);
    }
  }

  async function handleGenerateAllDisputes() {
    if (bulkGenerationLock.current) {
      console.log("Bulk letter generation already in progress.");
      return;
    }

    bulkGenerationLock.current = true;

    try {
      setUploading(true);
      setGenerationProgress("Preparing dispute letters...");
      setError("");
      setSuccessMessage("");

      if (!selectedClientId) {
        throw new Error("Please select a client first.");
      }

      const disputeAccounts = accounts.filter((account) => {
        return (
          account.dispute_candidate === true ||
          account.dispute_candidate === 1 ||
          account.dispute_candidate === "true"
        );
      });

      if (disputeAccounts.length === 0) {
        throw new Error(
          "No dispute-candidate accounts were found for this client."
        );
      }

      setGenerationProgress(
        `Generating ${disputeAccounts.length} dispute letters...`
      );

      const letters = await generateAllAccountDisputes(
        selectedClientId,
        disputeAccounts,
        bureau,
        (current, total) => {
          setGenerationProgress(
            `Generating ${current} of ${total} dispute letters...`
          );
        }
      );

      setGenerationProgress(
        `✅ ${letters.length} dispute letters generated successfully.`
      );

      setSuccessMessage(
        `${letters.length} dispute letters generated successfully.`
      );
    } catch (err) {
      console.error("Bulk dispute generation failed:", err);

      setGenerationProgress("");

      setError(
        err?.message ||
          "Failed to generate dispute letters"
      );
    } finally {
      bulkGenerationLock.current = false;
      setUploading(false);
    }
  }

  const filteredAccounts = accounts.filter((account) => {
    const protectAccount =
      account.protect_account === true ||
      account.protect_account === 1 ||
      account.protect_account === "true";

    const disputeCandidate =
      account.dispute_candidate === true ||
      account.dispute_candidate === 1 ||
      account.dispute_candidate === "true";

    const priority = String(account.priority || "").toLowerCase();

    if (accountFilter === "disputes") return disputeCandidate;
    if (accountFilter === "protected") return protectAccount;
    if (accountFilter === "high-priority") return priority === "high";

    return true;
  });

  return (
    <div
      style={{
        padding: "28px",
        background: "#f3f6fb",
        minHeight: "100vh",
      }}
    >
      <style>
        {`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>

      <div
        style={{
          marginBottom: "28px",
        }}
      >
        <div
          style={{
            fontSize: "18px",
            color: "#6b7280",
            marginBottom: "8px",
          }}
        >
          Integrity Suite CRM
        </div>

        <h1
          style={{
            fontSize: "64px",
            fontWeight: "800",
            color: "#0f172a",
            margin: 0,
          }}
        >
          Credit Reports
        </h1>
      </div>

      {openedCase && (
        <div
          style={{
            background: "#dcfce7",
            border: "1px solid #16a34a",
            borderRadius: "16px",      
            padding: "20px",
            marginBottom: "24px",
          }}
        >

          <OpenedCaseCard openedCase={openedCase} />

          <div
            style={{
              marginTop: "20px",
              paddingTop: "16px",
              borderTop: "1px solid #16a34a",
            }}
          >
            <h3 style={{ marginTop: 0 }}>🧠 Restored AI Session</h3>

            <div>
              <strong>Best Letter:</strong>{" "}
              {openedCase.full_analysis?.smartLetterRouting?.letterType}
            </div>
 
            <div>
              <strong>Routing Reason:</strong>{" "}
              {openedCase.full_analysis?.smartLetterRouting?.reason}
            </div>

            <div>
              <strong>CFPB Ready:</strong>{" "}
              {openedCase.full_analysis?.masterRecommendation?.cfpbReady}
            </div>

            <div>
              <strong>Attorney Demand Ready:</strong>{" "} 
              {openedCase.full_analysis?.masterRecommendation?.attorneyDemandReady}
            </div>

            <div style={{ marginTop: "12px" }}>
              <strong>Recommendation:</strong>{" "}
              {openedCase.full_analysis?.masterRecommendation?.recommendation}
            </div>

            <div
              style={{
                marginTop: "20px",
                paddingTop: "16px",
                borderTop: "1px solid #16a34a",
              }}
            >
              <h3 style={{ marginTop: 0 }}>
                📂 Evidence Checklist
              </h3>

              {(openedCase.evidence_needed || []).map((item, index) => (
                <div key={index}>
                  ✅ {item}
                </div>
              ))}

              <div
                style={{
                  marginTop: "20px",
                  paddingTop: "16px",
                  borderTop: "1px solid #16a34a",
                }}
              >
                <h3 style={{ marginTop: 0 }}>
                  ⚖ ️ Case Builder
                </h3>

                <div>
                  <strong>Case Status:</strong>{" "}
                  {openedCase.full_analysis?.caseBuilder?.caseStatus}
                </div>

                <div>
                  <strong>Priority:</strong>{" "}
                  {openedCase.full_analysis?.caseBuilder?.priority}
                </div>

                <div>
                  <strong>Recommended Letter:</strong>{" "}
                  {openedCase.full_analysis?.caseBuilder?.recommendedLetter}
                </div>

                <div>
                  <strong>Escalation:</strong>{" "}
                  {openedCase.full_analysis?.caseBuilder?.escalation}
                </div>

                <div>
                  <strong>Attorney Demand:</strong>{" "}
                  {openedCase.full_analysis?.caseBuilder?.attorneyDemand}
                </div>

                <div style={{ marginTop: "12px" }}>
                  <strong>Next Step:</strong>{" "}
                  {openedCase.full_analysis?.caseBuilder?.nextStep}
                </div>
              
                <div
                  style={{
                    marginTop: "20px",
                    paddingTop: "16px",
                    borderTop: "1px solid #16a34a",
                  }}
                >
                  <h3 style={{ marginTop: 0 }}>
                    📄 CFPB Packet
                  </h3>

                  <div>
                    <strong>Complaint Summary:</strong>{" "}
                    {openedCase.full_analysis?.cfpbPacket?.complaintSummary}
                  </div>

                  <div style={{ marginTop: "16px" }}>
                    <strong>Violations:</strong>
                  </div>

                  {(openedCase.full_analysis?.cfpbPacket?.violations || []).map(
                    (item, index) => (
                      <div key={index}>
                        ⚠ ️ {item}
                      </div>
                    )
                  )}

                  <div style={{ marginTop: "16px" }}>
                    <strong>Evidence:</strong>
                  </div>

                  {(openedCase.full_analysis?.cfpbPacket?.evidence || []).map(
                    (item, index) => (
                      <div key={index}>
                        ✅ {item}
                      </div>
                    )
                  )}

                  <div style={{ marginTop: "16px" }}>
                    <strong>Requested Resolution:</strong>{" "}
                    {openedCase.full_analysis?.cfpbPacket?.requestedResolution}
                  </div>

                  <div>
                    <strong>Escalation:</strong>{" "}
                    {openedCase.full_analysis?.cfpbPacket?.escalation}
                  </div>

                  <div
                    style={{
                      marginTop: "20px",
                      paddingTop: "16px",
                      borderTop: "1px solid #16a34a",
                    }}
                  >
                    <h3 style={{ marginTop: 0 }}>
                      ⚖ ️ Attorney Demand Packet
                    </h3>

                    <div>
                      <strong>Attorney Ready:</strong>{" "}  
                      {openedCase.full_analysis?.masterRecommendation?.attorneyDemandReady}
                    </div>

                    <div style={{ marginTop: "16px" }}>
                      <strong>Potential Violations:</strong>
                    </div>

                    <div>⚠️ {openedCase.primary_violation}</div>
                    <div>⚠️ FCRA 611 - Bureau investigation duties</div>
                    <div>⚠️ FCRA 623 - Furnisher accuracy duties</div>

                    <div style={{ marginTop: "16px" }}>
                      <strong>Supporting Evidence:</strong>
                    </div>

                    {(openedCase.evidence_needed || []).map((item, index) => (
                      <div key={index}>📂 {item}</div>
                    ))}

                    <div style={{ marginTop: "16px" }}>
                      <strong>Recommended Action:</strong>{" "}
                      Continue documenting dispute results. If the account is verified without proper evidence, prepare attorney demand review.
                    </div>

                    <div
                      style={{
                        marginTop: "20px",
                        paddingTop: "16px",
                        borderTop: "1px solid #16a34a",
                      }}
                    >
                      <h3 style={{ marginTop: 0 }}>
                        🧠 AI Action Center
                      </h3>

                      <div>
                        <strong>AI Confidence:</strong> 96%
                      </div>

                      <div>
                        <strong>Current Stage:</strong> Round 1 Investigation
                      </div>

                      <div>
                        <strong>Next Best Action:</strong>{" "}
                        {openedCase.full_analysis?.masterRecommendation?.cfpbReady === "YES"
                          ? "Generate CFPB Complaint"
                          : openedCase.full_analysis?.masterRecommendation?.attorneyDemandReady === "YES"
                          ? "Prepare Attorney Demand Packet"
                          : "Generate Next Dispute Letter"}
                      </div>

                      <div style={{ marginTop: "12px" }}>
                        <strong>Reason:</strong>{" "}
                        {openedCase.full_analysis?.smartLetterRouting?.reason ||
                          openedCase.full_analysis?.masterRecommendation?.recommendation}
                      </div>

                      <div style={{ marginTop: "16px" }}>
                        <strong>Quick Actions:</strong>
                      </div>

                      <button
                        type="button"
                        onClick={handleGenerateOpenedCaseLetter}
                        disabled={uploading}
                        style={{
                          marginTop: "10px",
                          marginRight: "10px",
                          padding: "10px 14px",
                          borderRadius: "10px",
                          border: "none",
                          background: "#D4AF37",
                          color: "#111111",
                          fontWeight: "700",
                          cursor: uploading
                            ? "not-allowed"
                            : "pointer",
                          opacity: uploading
                            ? 0.65
                            : 1,
                        }}
                      >
                        {uploading
                          ? "Generating Letter..."
                          : "📄 Generate Letter"}
                      </button>

                      <button
                        type="button"
                        style={{
                          marginTop: "10px",
                          marginRight: "10px",
                          padding: "10px 14px",
                          borderRadius: "10px",
                          border: "none",
                          background: "#dc2626",
                          color: "white",
                          fontWeight: "700",
                          cursor: "pointer",
                        }}
                      >
                        📬 Generate CFPB Complaint
                      </button>

                      <button
                        type="button"
                        style={{
                          marginTop: "10px",
                          padding: "10px 14px",
                          borderRadius: "10px",
                          border: "none",
                          background: "#D4AF37",
                          color: "white",
                          fontWeight: "700",
                          cursor: "pointer",
                        }}
                      >
                        ⚖ Generate Attorney Demand
                      </button>
                    </div>

                  </div>

                </div>

              </div>

            </div>

          </div>

        </div>
       )}

      <div
        style={{
        background: "white",
        borderRadius: "22px",
        padding: "22px",
        border: "1px solid #e5e7eb",
        marginBottom: "24px",
        boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
      }}
    >
      <label
        style={{
          display: "block",
          fontSize: "15px",
          fontWeight: "800",
          color: "#334155",
          marginBottom: "10px",
        }}
      >
        Select Client
      </label>

      <select
        value={selectedClientId}
        onChange={(e) => setSelectedClientId(e.target.value)}
        style={{
          width: "100%",
          padding: "16px",
          borderRadius: "16px",
          border: "1px solid #cbd5e1",
          fontSize: "16px",
          background: "white",
        }}
      >
        <option value="">All Clients</option>

        {clients.map((client) => (
          <option key={client.id} value={client.id}>
            {client.full_name || client.name || `Client ${client.id}`}
          </option>
        ))}
      </select>
    </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "16px",
          marginBottom: "28px",
        }}
      >
        {[
          {
            label: "📊 Total Accounts",
            value: reportStats.total,
            color: "#A67C00",
          },
          {
            label: "🟢 Protect",
            value: reportStats.protect,
            color: "#16a34a",
          },
          {
            label: "🔴 Dispute Candidates",
            value: reportStats.disputeCandidates,
            color: "#dc2626",
          },
          {
            label: "🚨 High Priority",
            value: reportStats.highPriority,
            color: "#f59e0b",
          },
          {
            label: "🧠 AI Confidence",
            value: `${reportStats.averageConfidence}%`,
            color: "#D4AF37",
          },
        ].map((item) => (
          <div
            key={item.label}
            style={{
              background: "white",
              borderRadius: "22px",
              padding: "22px",
              border: "1px solid #e5e7eb",
              boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
            }}
          >
            <div
              style={{
                fontSize: "15px",
                color: item.color,
                fontWeight: "700",
                marginBottom: "10px",
              }}
            >
              {item.label}
            </div>

            <div
              style={{
                fontSize: "40px",
                fontWeight: "900",
                color: "#0f172a",
                lineHeight: 1,
              }}
            >
              {item.value}
            </div>
          </div>
        ))}
      </div>
      
      <div
        style={{
          background: "white",
          borderRadius: "22px",
          padding: "24px",
          border: "1px solid #e5e7eb",
          marginBottom: "28px",
          boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
        }}
      >
        <div
          style={{
            fontSize: "28px",
            fontWeight: "800",
            color: "#0f172a",
            marginBottom: "16px",
          }}
        >
          Credit Audit Summary
        </div>

        <div style={{ fontSize: "18px", marginBottom: "10px" }}>
          Collections Found: {auditSummary.collections}
        </div>

        <div style={{ fontSize: "18px", marginBottom: "10px" }}>
          Charge-Offs Found: {auditSummary.chargeOffs}
        </div>

        <div style={{ fontSize: "18px", marginBottom: "10px" }}>
          Inquiries Found: {auditSummary.inquiries}
        </div>

        <div
          style={{
            fontSize: "20px",
            fontWeight: "800",
            color:
              auditSummary.riskLevel === "High"
                ? "#dc2626"
                : auditSummary.riskLevel === "Medium"
                ? "#f59e0b"
                : "#16a34a",
          }}
        >
          Risk Level: {auditSummary.riskLevel}
        </div>
      </div>

      <div
        style={{
          background: "linear-gradient(135deg, #0B0B0B 0%, #1A1A1A 100%)",
          color: "white",
          borderRadius: "22px",
          padding: "30px",
          marginBottom: "28px",
          boxShadow: "0 14px 30px rgba(15,23,42,0.25)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "20px",
            flexWrap: "wrap",
            marginBottom: "24px",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "30px",
                fontWeight: "900",
                marginBottom: "8px",
              }}
            >
              🧠 AI Executive Summary
            </div>

            <div
              style={{
                fontSize: "15px",
                color: "#cbd5e1",
              }}
            >
              AI-powered analysis and recommended next steps
            </div>
          </div>

          <div
            style={{
              background:
                reportStats.disputeCandidates > reportStats.protect
                  ? "rgba(239,68,68,0.18)"
                  : reportStats.averageConfidence >= 80
                  ? "rgba(34,197,94,0.18)"
                  : reportStats.averageConfidence >= 65
                  ? "rgba(212,175,55,0.18)"
                  : "rgba(245,158,11,0.18)",
              border: "1px solid rgba(255,255,255,0.20)",
              borderRadius: "16px",
              padding: "14px 18px",
              minWidth: "190px",
            }}
          >
            <div
              style={{
                fontSize: "13px",
                fontWeight: "800",
                color: "#cbd5e1",
                marginBottom: "6px",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              Overall Credit Health
            </div>

            <div
              style={{
                fontSize: "24px",
                fontWeight: "900",
              }}
            >
              {reportStats.total === 0
                ? "No Data"
                : reportStats.disputeCandidates > reportStats.protect
                ? "🔴 Needs Improvement"
                : reportStats.averageConfidence >= 80
                ? "🟢 Excellent"
                : reportStats.averageConfidence >= 65
                ? "🟡 Good"
                : "🟡 Fair"}
            </div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "14px",
            marginBottom: "24px",
          }}
        >
          {[
            {
              label: "Accounts Analyzed",
              value: reportStats.total,
              icon: "📊",
            },
            {
              label: "Positive Accounts",
              value: reportStats.protect,
              icon: "🛡️",
            },
            {
              label: "Recommended Disputes",
              value: reportStats.disputeCandidates,
              icon: "⚠️",
            },
            {
              label: "High Priority",
              value: reportStats.highPriority,
              icon: "🚨",
            },
            {
              label: "AI Confidence",
              value: `${reportStats.averageConfidence}%`,
              icon: "🎯",
            },
          ].map((item) => (
            <div
              key={item.label}
              style={{
                background: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: "16px",
                padding: "18px",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  color: "#cbd5e1",
                  fontWeight: "700",
                  marginBottom: "8px",
                }}
              >
                {item.icon} {item.label}
              </div>

              <div
                style={{
                  fontSize: "30px",
                  fontWeight: "900",
                }}
              >
                {item.value}
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "18px",
          }}
        >
          <div
            style={{
              background: "rgba(255,255,255,0.10)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: "18px",
              padding: "20px",
            }}
          >
            <div
              style={{
                fontWeight: "900",
                fontSize: "19px",
                marginBottom: "14px",
              }}
            >
              🎯 Next Best Action
            </div>

            <div
              style={{
                fontSize: "18px",
                fontWeight: "800",
                marginBottom: "10px",
              }}
            >
              {reportStats.total === 0
                ? "Import a credit report to begin the analysis."
                : reportStats.highPriority > 0
                ? "Review high-priority accounts before generating letters."
                : reportStats.disputeCandidates > 0
                ? "Generate Round 1 dispute letters."
                : "Protect positive accounts and continue monitoring."}
            </div>

            <div
              style={{
                fontSize: "15px",
                color: "#cbd5e1",
                lineHeight: 1.7,
              }}
            >
              {reportStats.total === 0
                ? "The AI summary will update automatically after accounts are imported."
                : reportStats.highPriority > 0
                ? `${reportStats.highPriority} account${
                    reportStats.highPriority === 1 ? "" : "s"
                  } require immediate review.`
                : reportStats.disputeCandidates > 0
                ? `${reportStats.disputeCandidates} account${
                    reportStats.disputeCandidates === 1 ? "" : "s"
                  } are currently recommended for dispute.`
                : "No immediate dispute candidates were detected in this analysis."}
            </div>
          </div>

          <div
            style={{
              background: "rgba(255,255,255,0.10)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: "18px",
              padding: "20px",
            }}
          >
            <div
              style={{
                fontWeight: "900",
                fontSize: "19px",
                marginBottom: "14px",
              }}
            >
              📄 Recommended Letter
            </div>

            <div
              style={{
                fontSize: "18px",
                fontWeight: "800",
                marginBottom: "10px",
              }}
            >
              {reportStats.disputeCandidates > 0
                ? "General Dispute Letter — Round 1"
                : "No Dispute Letter Recommended"}
            </div>

            <div
              style={{
                fontSize: "15px",
                color: "#cbd5e1",
                lineHeight: 1.7,
              }}
            >
              {reportStats.disputeCandidates > 0
                ? "Begin with a factual first-round dispute before using Method of Verification or escalation letters."
                : "Continue protecting positive tradelines and review the report for future changes."}
            </div>
          </div>
        </div>

        <div
          style={{
            marginTop: "18px",
            background: "rgba(255,255,255,0.10)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: "18px",
            padding: "20px",
          }}
        >
          <div
            style={{
              fontWeight: "900",
              fontSize: "19px",
              marginBottom: "14px",
            }}
          >
            📋 AI Recommended Strategy
          </div>

          <ol
            style={{
              margin: 0,
              paddingLeft: "24px",
              lineHeight: 1.9,
              fontSize: "16px",
            }}
          >
            <li>Protect all positive tradelines from unnecessary disputes.</li>

            {reportStats.disputeCandidates > 0 && (
              <li>
                Review the {reportStats.disputeCandidates} recommended dispute
                candidates for accuracy and supporting evidence.
              </li>
            )}

            {reportStats.highPriority > 0 ? (
              <li>
                Address the {reportStats.highPriority} high-priority account
                {reportStats.highPriority === 1 ? "" : "s"} first.
              </li>
            ) : (
              <li>
                No high-priority accounts were detected. Begin with the strongest
                dispute candidates.
              </li>
            )}

            <li>
              Prioritize collections, charge-offs, late payments, and then
              inquiries.
            </li>

            <li>
              Generate Round 1 letters before moving to Method of Verification
              or escalation.
            </li>

            <li>
              Re-import the updated report after each dispute round to measure
              progress.
            </li>
          </ol>
        </div>
      </div>

      <AIActionCenter
        selectedClientId={selectedClientId}
        reportStats={reportStats}
        loading={loading}
        accounts={accounts}
        handleGenerateAllDisputes={handleGenerateAllDisputes}
        loadData={loadData}
        setError={setError}
        setSuccessMessage={setSuccessMessage}
      />

      <div
        id="credit-report-accounts"
        style={{
          background: "white",
          borderRadius: "22px",
          padding: "24px",
          border: "1px solid #e5e7eb",
          marginBottom: "28px",
          boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
        }}
      >
        <div
          style={{
            fontSize: "28px",
            fontWeight: "800",
            color: "#0f172a",
            marginBottom: "20px",
          }}
        >
          Credit Report Accounts
        </div>

        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
            marginBottom: "20px",
          }}
        >
          <button
            type="button"
            onClick={() => setAccountFilter("all")}
            style={{
              border:
                accountFilter === "all"
                  ? "1px solid #D4AF37"
                  : "1px solid #cbd5e1",
              background:
                accountFilter === "all" ? "#D4AF37" : "#ffffff",
              color: accountFilter === "all" ? "#111111" : "#334155",
              borderRadius: "12px",
              padding: "10px 14px",
              fontSize: "14px",
              fontWeight: "800",
              cursor: "pointer",
            }}
          >
            All Accounts ({accounts.length})
          </button>

          <button
            type="button"
            onClick={() => setAccountFilter("disputes")}
            style={{
              border:
                accountFilter === "disputes"
                  ? "1px solid #dc2626"
                  : "1px solid #fecaca",
              background:
                accountFilter === "disputes" ? "#dc2626" : "#fef2f2",
              color:
                accountFilter === "disputes" ? "#ffffff" : "#b91c1c",
              borderRadius: "12px",
              padding: "10px 14px",
              fontSize: "14px",
              fontWeight: "800",
              cursor: "pointer",
            }}
          >
            Dispute Candidates ({reportStats.disputeCandidates})
          </button>

          <button
            type="button"
            onClick={() => setAccountFilter("protected")}
            style={{
              border:
                accountFilter === "protected"
                  ? "1px solid #16a34a"
                  : "1px solid #bbf7d0",
              background:
                accountFilter === "protected" ? "#16a34a" : "#f0fdf4",
              color:
                accountFilter === "protected" ? "#ffffff" : "#15803d",
              borderRadius: "12px",
              padding: "10px 14px",
              fontSize: "14px",
              fontWeight: "800",
              cursor: "pointer",
            }}
          >
            Protected Accounts ({reportStats.protect})
          </button>

          <button
            type="button"
            onClick={() => setAccountFilter("high-priority")}
            style={{
              border:
                accountFilter === "high-priority"
                  ? "1px solid #ea580c"
                  : "1px solid #fed7aa",
              background:
                accountFilter === "high-priority"
                  ? "#ea580c"
                  : "#fff7ed",
              color:
                accountFilter === "high-priority"
                  ? "#ffffff"
                  : "#c2410c",
              borderRadius: "12px",
              padding: "10px 14px",
              fontSize: "14px",
              fontWeight: "800",
              cursor: "pointer",
            }}
          >
            High Priority ({reportStats.highPriority})
          </button>
        </div>

        {filteredAccounts.length === 0 ? (
          <div
            style={{
            color: "#64748b",
            fontSize: "16px",
          }}
        >
          No imported accounts found.
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gap: "14px",
          }}
        >
          
          <div
            style={{
              marginBottom: "20px",
            }}
          >
            <button
              id="generate-all-dispute-letters"
              type="button"
              onClick={handleGenerateAllDisputes}
              disabled={uploading}
              style={{
                width: "100%",
                padding: "18px",
                borderRadius: "16px",
                border: "none",
                background: uploading ? "#15803d" : "#16a34a",
                color: "white",
                fontSize: "20px",
                fontWeight: "800",
                cursor: uploading ? "not-allowed" : "pointer",
                opacity: uploading ? 0.85 : 1,
                transition: "all .2s ease",
              }}
            >
              {uploading ? (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "12px",
                  }}
                >
                  <span
                    style={{
                      width: "18px",
                      height: "18px",
                      border: "3px solid rgba(255,255,255,0.4)",
                      borderTop: "3px solid white",
                      borderRadius: "50%",
                      display: "inline-block",
                      animation: "spin 0.8s linear infinite",
                      flexShrink: 0,
                    }}
                  />

                  {generationProgress || "Generating Dispute Letters..."}
                </span>
              ) : (
                "Generate ALL Dispute Letters"
              )}                   
            </button>
          </div>

            {filteredAccounts.map((account) => {
              const audit = getSmartAudit(account);
              const metro2 = getMetro2Violations(account);
              const fcra = getFcraViolations(account);
              const cfpb = getCfpbRecommendation(account, audit, fcra);
              const roundPlan = getLetterRoundPlan(
                account,
                audit,
                cfpb
              );

              const litigation = getLitigationAnalysis(
                account,
                fcra,
                cfpb
              );              
   
              const arbitration = getArbitrationAnalysis(
                account,
                litigation
              );

              const evidence = getEvidenceVault(
                account,
                litigation,
                arbitration
              );

              const caseValue = getCaseValue(
                account,
                litigation,
                arbitration,
                evidence
              );

              const readiness = getReadinessScore(
                audit,
                fcra,
                cfpb,
                litigation,
                evidence
              );

              const attorneyDemand = getAttorneyDemandEngine(
                account,
                fcra,
                litigation,
                evidence,
                caseValue
              );

              const statute = getStatuteLimitations(account);

              const identityTheft = getIdentityTheftAnalysis(account);

              const ftc = getFtcAffidavitEngine(identityTheft);

              const cfpbEscalation =
                getCfpbEscalationEngine(
                  account,
                  fcra,
                  litigation,
                  evidence
                );

              const furnisherDispute = getFurnisherDisputeEngine(
                account,
                fcra,
                litigation,
                evidence
              );     

              const metro2Compliance = getMetro2ComplianceEngine(
                account,
                fcra,
                furnisherDispute
              );

              const duplicateAccount = getDuplicateAccountEngine(account);

              const reAging = getReAgingDetectionEngine(
                account,
                statute,
                metro2Compliance
              );

              const zombieDebt = getZombieDebtEngine(
                account,
                statute
              );

              const bankruptcyViolation = getBankruptcyViolationEngine(
                account
              );

              const chargeOffAccuracy = getChargeOffAccuracyEngine(
                account
              );

              const collectionAuthority = getCollectionAuthorityEngine(
                account
              );

              const mixedFile = getMixedFileEngine(
                account
              );

              const secondaryBureau = getSecondaryBureauEngine(
                account
              );

              const inquiryPermissiblePurpose =
                getInquiryPermissiblePurposeEngine(
                  account
                );

              const dofdViolation =
                getDofdViolationEngine(
                  account
                );

              const paymentHistoryConsistency =
                getPaymentHistoryConsistencyEngine(
                  account
                );

              const utilizationManipulation =
                getUtilizationManipulationEngine(
                  account
                );

              const creditorNameConsistency =
                getCreditorNameConsistencyEngine(
                  account
                );

              const accountStatusContradiction =
                getAccountStatusContradictionEngine(
                  account
                );

              const balanceAccuracy =
                getBalanceAccuracyEngine(
                  account
                );

              const dateLastReported =
                getDateLastReportedEngine(
                  account
                );

              const highUtilizationRisk =
                getHighUtilizationRiskEngine(
                  account
                );

              const accountAgeManipulation =
                getAccountAgeManipulationEngine(
                  account
                );

              const creditLimitAccuracy =
                getCreditLimitAccuracyEngine(
                  account
                );

              const monthlyPaymentAccuracy =
                getMonthlyPaymentAccuracyEngine(
                  account
                );

              const pastDueAmountAccuracy =
                getPastDueAmountAccuracyEngine(
                  account
                );

              const highCreditAmountAccuracy =
                getHighCreditAmountAccuracyEngine(
                  account
                );

              const masterRecommendation =
                getMasterRecommendationEngine(
                  account,
                  [
                   audit,
                   metro2Compliance,
                   bankruptcyViolation,
                   chargeOffAccuracy,
                   collectionAuthority,
                   mixedFile,
                   secondaryBureau,
                   inquiryPermissiblePurpose,
                   dofdViolation,
                   paymentHistoryConsistency,
                   utilizationManipulation,
                   creditorNameConsistency,
                   accountStatusContradiction,
                   balanceAccuracy,
                   dateLastReported,
                   highUtilizationRisk,
                   accountAgeManipulation,
                   creditLimitAccuracy,
                   monthlyPaymentAccuracy,
                   pastDueAmountAccuracy,
                   highCreditAmountAccuracy,
                  ]
                );

              const evidenceChecklist =
                getEvidenceChecklistEngine(
                  account,
                  masterRecommendation.primaryViolation
                );

              const smartLetterRouting =
                getSmartLetterRoutingEngine(
                  account,
                  masterRecommendation
                );

              const caseBuilder =
                getCaseBuilderEngine(
                  account,
                  masterRecommendation,
                  evidenceChecklist,
                  smartLetterRouting
                );

              const cfpbPacket =
                getCFPBPacketEngine(
                  account,
                  masterRecommendation,
                  evidenceChecklist,
                  caseBuilder
                );

              return (
                <div
                  key={account.id}
                  onClick={() => {
                    setSelectedAccount({
                      ...account,
                      recommended_letter:
                        caseBuilder.recommendedLetter ||
                        smartLetterRouting.letterType ||
                        "General Dispute",
                    });
                    setInspectorOpen(true);
                  }}
                  style={{
                  cursor: "pointer",
                  border: "1px solid #e5e7eb",
                  borderRadius: "16px",
                  padding: "18px",
                  background: "#f8fafc",
                }}
              >
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "800",
                  color: "#0f172a",
                  marginBottom: "8px",
                }}
              >
                {account.creditor}
              </div>

              <div style={{ marginBottom: "4px" }}>
                Type: {account.account_type}
              </div>

              <div style={{ marginBottom: "4px" }}>
                Status: {account.status}
              </div>

              <div
                style={{
                  color: "#A67C00",
                  fontWeight: "700",
                }}
              >
                Suggested Action: {account.suggested_action}
              </div>

              <div
                style={{
                  marginTop: "14px",
                  padding: "14px",
                  background: "#ffffff",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                }}
              >
              
                {/* AI Account Intelligence */}
                <div
                  style={{
                    marginBottom: "16px",
                    padding: "14px",
                    borderRadius: "12px",
                    background: account.protect_account
                      ? "#ecfdf5"
                      : account.dispute_candidate
                      ? "#fef2f2"
                      : "#FFF8E1",
                    border: `2px solid ${
                      account.protect_account
                        ? "#16a34a"
                        : account.dispute_candidate
                        ? "#dc2626"
                        : "#D4AF37"
                   }`,
                 }}
               >
                 <div
                   style={{
                     fontSize: "18px",
                     fontWeight: "800",
                     marginBottom: "10px",
                     color: account.protect_account
                       ? "#15803d"
                       : account.dispute_candidate
                       ? "#b91c1c"
                       : "#A67C00",
                   }}
                 >
                   {account.protect_account
                     ? "🟢 Protect This Account"
                     : account.dispute_candidate
                     ? "🔴 Dispute Candidate"
                     : "🟡 Manual Review"}
                 </div>

                 <div style={{ marginBottom: "6px" }}>
                   <strong>Strategy:</strong>{" "}
                   {account.strategy || "General Accuracy Review"}
                 </div>

                 <div style={{ marginBottom: "6px" }}>
                   <strong>Confidence:</strong>{" "}
                   {account.confidence_score || 0}% (
                   {account.confidence_label || "Unknown"})
                 </div>

                 <div style={{ marginBottom: "6px" }}>
                   <strong>Reason:</strong>{" "}
                   {account.review_reason || "No recommendation available."}
                 </div>

                 {account.flags?.length > 0 && (
                   <div>
                     <strong>Flags:</strong>

                     <div
                       style={{
                         display: "flex",
                         flexWrap: "wrap",
                         gap: "8px",
                         marginTop: "8px",
                       }}
                     >
                       {account.flags.map((flag, index) => (
                         <span
                           key={index}
                           style={{
                             background: "#dbeafe",
                             color: "#B8860B",
                             padding: "4px 10px",
                             borderRadius: "999px",
                             fontSize: "13px",
                             fontWeight: "700",
                           }}
                         >
                           {flag}
                         </span>
                       ))}
                     </div>
                   </div>
                 )}
               </div>                
                 
                <div
                  style={{
                    fontWeight: "800",
                    marginBottom: "10px",
                    color:
                      audit.risk === "High"
                        ? "#dc2626"
                        : audit.risk === "Medium"
                        ? "#f59e0b"
                        : "#16a34a",
                  }}
                >
                  AI Risk Level: {audit.risk}
                </div>

                <div style={{ marginBottom: "8px" }}>
                  Next Letter: {audit.nextLetter}
                </div>

                <div style={{ fontWeight: "700" }}>AI Strategy:</div>

                <div>{audit.strategy}</div>

                <div style={{ marginTop: "16px" }}>
                  <div
                    style={{
                      fontWeight: "800",
                      color: "#D4AF37",
                      marginBottom: "8px",
                    }}
                  >
                    Metro 2 Review
                  </div>

                  {metro2.map((issue, index) => (
                    <div
                      key={index}
                      style={{
                      marginBottom: "6px",
                      color: "#475569",
                   }}
                 >
                   ✓ {issue}
                 </div>
               ))}
             </div>

             </div>

             <div
               style={{
                 marginTop: "16px",
                 paddingTop: "16px",
                 borderTop: "1px solid #e2e8f0",
               }}
             >
               <div
                 style={{
                   fontWeight: "800",
                   color: "#dc2626",
                   marginBottom: "8px",
                 }}
               >
                 FCRA Review
               </div>

               <div
                 style={{
                   marginBottom: "8px",
                   fontWeight: "700",
                 }}
               >
                 Confidence Score: {fcra.confidence}%
               </div>

               <div
                 style={{
                   fontWeight: "700",
                   marginBottom: "8px",
                 }}
               >
                 Potential Violations
               </div>

               {fcra.violations.map((issue, index) => (
                 <div
                   key={index}
                   style={{
                     marginBottom: "6px",
                     color: "#475569",
                   }}
                 >
                   ⚠ {issue}
                 </div>
               ))}

               <div
                 style={{
                   marginTop: "12px",
                   fontWeight: "700",
                 }}
               >
                 Applicable Laws
               </div>

               {fcra.laws.map((law, index) => (
                 <div
                   key={index}
                   style={{
                     color: "#D4AF37",
                   }}
                 >
                   📚 {law}
                 </div>
               ))}
             </div>

             <hr
               style={{
                 marginTop: "18px",
                 marginBottom: "18px",
                 border: "1px solid #e5e7eb",
               }}
             />

             <div
               style={{
                 color:
                   cfpb.urgency === "High"
                     ? "#dc2626"
                     : cfpb.urgency === "Medium"
                     ? "#f59e0b"
                     : "#16a34a",
                 fontWeight: "800",
                 marginBottom: "10px",
               }}
             >
               CFPB Recommendation
             </div>

             <div
               style={{
                 marginBottom: "8px",
                 fontWeight: "700",
               }}
             >
               Urgency: {cfpb.urgency}
             </div>

             <div
               style={{
                 marginBottom: "12px",
                 color: "#475569",
               }}
             >
               {cfpb.reason}
             </div>

             <div
               style={{
                 fontWeight: "700",
                 color: "#D4AF37",
               }}
             >
               Complaint Type
             </div>

             <div>
               {cfpb.complaintType}
             </div>             

             <hr
               style={{
                 marginTop: "30px",
                 marginBottom: "30px",
                 borderColor: "#e2e8f0",
              }}
            />

            <div
              style={{
                color: "#D4AF37",
                fontWeight: "800",
                fontSize: "32px",
                marginBottom: "20px",
              }}
            >
              Letter Round Plan
            </div>

            <div
              style={{
                marginBottom: "14px",
                fontWeight: "700",
              }}
            >
              Current Round: {roundPlan.currentRound}
            </div>

            <div
              style={{
                marginBottom: "14px",
              }}
            >
              Current Letter:
              {" "}
              {roundPlan.currentLetter}
            </div>

            <div
              style={{
                marginBottom: "14px",
              }}
            >
              Next Round:
              {" "}
              {roundPlan.nextRound}
            </div>

            <div
              style={{
                marginBottom: "14px",
              }}
            >
              Next Letter:
              {" "}
              {roundPlan.nextLetter}
            </div>

            <div
              style={{
                marginBottom: "14px",
              }}
            >
              Wait Days:
              {" "}
              {roundPlan.waitDays}
            </div>
 
            <div
              style={{
                fontWeight: "700",
                marginBottom: "8px",
              }}
            >
              Round Strategy
            </div>

            <div
              style={{
                color: "#475569",
              }}
            >
              {roundPlan.strategy}
            </div>             

            <hr
              style={{
                marginTop: "30px",
                marginBottom: "30px",
                borderColor: "#cbd5e1",
              }}
            />

            <div
              style={{
                fontSize: "22px",
                fontWeight: "800",
                color: "#dc2626",
                marginBottom: "20px",
              }}
            >
              Litigation Analysis
            </div>

            <div style={{ marginBottom: "12px", fontWeight: "700" }}>
              Litigation Risk: {litigation.risk}
            </div>

            <div style={{ marginBottom: "12px" }}>
              Case Strength: {litigation.strength}
            </div>

            <div style={{ fontWeight: "700", marginBottom: "8px" }}>
              Potential Causes of Action
            </div>

            {litigation.causes.map((cause, index) => (
              <div
                key={index}
                style={{
                  marginBottom: "6px",
                  color: "#475569",
                }}
              >
                ⚖️ {cause}
              </div>
            ))}

            <div
              style={{
                marginTop: "18px",
                marginBottom: "8px",
                fontWeight: "700",
              }}
            >
              Recommendation
            </div>

            <div style={{ color: "#475569" }}>
              {litigation.recommendation}
            </div>

            <hr
              style={{
                marginTop: "30px",
                marginBottom: "30px",
                borderColor: "#cbd5e1",
              }}
            />

            <div
              style={{
                fontSize: "22px",
                fontWeight: "800",
                color: "#D4AF37",
                marginBottom: "20px",
              }}
            >
              Arbitration Analysis
            </div>

            <div
              style={{
                marginBottom: "12px",
                fontWeight: "700",
              }}
            >
              Arbitration Strength: {arbitration.strength}
            </div>

            <div
              style={{
                marginBottom: "12px",
                fontWeight: "700",
              }}
            >
              Forum: {arbitration.forum}
            </div>

            <div
              style={{
                marginBottom: "10px",
               fontWeight: "700",
             }}
           >
             Contract Issues
           </div>

           {arbitration.clauses.map((clause, index) => (
             <div
               key={index}
               style={{
                 marginBottom: "6px",
                 color: "#475569",
               }}
             >
               ⚖️ {clause}
             </div>
           ))}

           <div
             style={{
               marginTop: "20px",
               marginBottom: "8px",
               fontWeight: "700",
             }}
           >
             Recommendation
           </div>

           <div
             style={{
               color: "#475569",
             }}
           >
             {arbitration.recommendation}
           </div>

           <hr
             style={{
               marginTop: "24px",
               marginBottom: "24px",
             }}
           />

           <div>
             <div
               style={{
                 fontSize: "24px",
                 fontWeight: "800",
                 color: "#D4AF37",
                 marginBottom: "20px",
               }}
             >
               Evidence Vault
             </div>

             <div
               style={{
                 fontWeight: "700",
                 marginBottom: "14px",
               }}
             >
               Case Ready:
               {" "}
               {evidence.caseReady ? "YES" : "NO"}
             </div>

             <div
               style={{
                 fontWeight: "700",
                 marginBottom: "14px",
               }}
             >
               Evidence Strength:
               {" "}
               {evidence.strength}
             </div>

             <div
               style={{
                 fontWeight: "700",
                 marginBottom: "10px",
               }}
             >
               Missing Documents
             </div>

             {evidence.missingDocuments.map((doc, index) => (
               <div
                 key={index}
                 style={{
                   color: "#475569",
                   marginBottom: "8px",
                 }}
               >
                 📁 {doc}
               </div>
             ))}

             <div
               style={{
                 marginTop: "20px",
                 fontWeight: "700",
                 marginBottom: "8px",
               }}
             >
               Next Evidence Step
             </div>

             <div
               style={{
                 color: "#475569",
               }}
             >
               {evidence.nextEvidenceStep}
             </div>
           </div>

             <hr
               style={{
                 marginTop: "24px",
                 marginBottom: "24px",
               }}
             />

             <div>
               <div
                 style={{
                   fontSize: "24px",
                   fontWeight: "800",
                   color: "#16a34a",
                   marginBottom: "20px",
                 }}
               >
                 Case Value Analysis
               </div>

               <div
                 style={{
                   fontWeight: "700",
                   marginBottom: "14px",
                 }}
               >
                 Estimated Value:
                 {" "}
                 {caseValue.value}
               </div>

               <div
                 style={{
                   fontWeight: "700",
                   marginBottom: "14px",
                 }}
               >
                 Case Strength:
                 {" "}
                 {caseValue.strength}
               </div>

               <div
                 style={{
                   fontWeight: "700",
                   marginBottom: "14px",
                 }}
               >
                 Attorney Recommendation:
                 {" "}
                 {caseValue.attorney}
               </div>

               <div
                 style={{
                   fontWeight: "700",
                   marginBottom: "10px",
                 }}
               >
                 Potential Damages
               </div>

               {caseValue.damages.map((damage, index) => (
                 <div
                   key={index}
                   style={{
                     color: "#475569",
                     marginBottom: "8px",
                   }}
                 >
                   💰 {damage}
                 </div>
               ))}
             </div>             

              <div
                style={{
                  borderTop: "2px solid #94a3b8",
                  marginTop: "30px",
                  paddingTop: "25px",
                }}
              >
                <div
                  style={{
                    fontSize: "28px",
                    fontWeight: "800",
                    color: "#0f766e",
                    marginBottom: "20px",
                  }}
                >
                  Lawsuit Readiness Score
                </div>

                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    marginBottom: "16px",
                  }}
                >
                  Score: {readiness.score}/100
                </div>

                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    marginBottom: "16px",
                  }}
                >
                  Rating: {readiness.rating}
                </div>

                <div
                  style={{
                    color: "#475569",
                    fontSize: "18px",
                  }}
                >
                  Overall readiness for CFPB escalation, arbitration, or litigation.
                </div>
              </div>

              <hr
                style={{
                  marginTop: "30px",
                  marginBottom: "30px",
                  borderColor: "#cbd5e1",
                }}
              />

              <div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "800",
                    color: "#dc2626",
                    marginBottom: "20px",
                  }}
                >
                  Attorney Demand Analysis
                </div>

                <div
                  style={{
                    fontWeight: "700",
                    marginBottom: "12px",
                  }}
                >
                  Demand Recommended:
                  {" "}
                  {attorneyDemand.recommended}
                </div>

                <div
                  style={{
                    fontWeight: "700",
                    marginBottom: "12px",
                  }}
                >
                  Demand Level:
                  {" "}
                  {attorneyDemand.demandLevel}
                </div>

                <div
                  style={{
                    fontWeight: "700",
                    marginBottom: "12px",
                  }}
                >
                  Demand Amount:
                  {" "}
                  {attorneyDemand.demandAmount}
                </div>

                <div
                  style={{
                    fontWeight: "700",
                    marginBottom: "12px",
                  }}
                >
                  Response Days:
                  {" "}
                  {attorneyDemand.responseDays}
                </div>

                <div
                  style={{
                    fontWeight: "700",
                    marginBottom: "10px",
                  }}
                >
                  Leverage Factors
                </div>

                {attorneyDemand.leverage.map((factor, index) => (
                  <div
                    key={index}
                    style={{
                      color: "#475569",
                      marginBottom: "8px",
                    }}
                  >
                    ⚖️ {factor}
                  </div>
                ))}

                <div
                  style={{
                    marginTop: "18px",
                    fontWeight: "700",
                    marginBottom: "8px",
                  }}
                >
                  Strategy
                </div>

                <div
                  style={{
                    color: "#475569",
                  }}
                >
                  {attorneyDemand.strategy}
                </div>
              </div>

              <div
                style={{
                  borderTop: "1px solid #94a3b8",
                  marginTop: "40px",
                  paddingTop: "30px",
                }}
              >
                <div
                   style={{
                     color: "#D4AF37",
                     fontWeight: "800",
                     fontSize: "22px",
                     marginBottom: "20px",
                   }}
                 >
                   Statute of Limitations
                 </div>

                 <div
                   style={{
                     marginBottom: "14px",
                     fontWeight: "700",
                   }}
                 >
                   State:
                   {" "}
                   {statute.state}
                 </div>

                 <div
                   style={{
                     marginBottom: "14px",
                     fontWeight: "700",
                   }}
                 >
                   Account Type:
                   {" "}
                   {statute.accountType}
                 </div>

                 <div
                   style={{
                     marginBottom: "14px",
                     fontWeight: "700",
                   }}
                 >
                   Years:
                   {" "}
                   {statute.years}
                 </div>

                 <div
                   style={{
                     marginBottom: "14px",
                     fontWeight: "700",
                   }}
                 >
                   Risk Level:
                   {" "}
                   {statute.risk}
                 </div>

                 <div
                   style={{
                     fontWeight: "700",
                     marginBottom: "8px",
                   }}
                 >
                   Recommendation
                 </div>

                 <div
                   style={{
                     color: "#475569",
                   }}
                 >
                   {statute.recommendation}
                 </div>
               </div>

               <div
                 style={{
                   borderTop: "1px solid #94a3b8",
                   marginTop: "40px",
                   paddingTop: "30px",
                 }}
               >
                 <div
                   style={{
                     color: "#D4AF37",
                     fontWeight: "800",
                     fontSize: "22px",
                     marginBottom: "20px",
                   }}
                 >
                   Identity Theft Analysis
                 </div>

                 <div
                   style={{
                     marginBottom: "14px",
                     fontWeight: "700",
                   }}
                 >
                   Risk Level:
                   {" "}
                   {identityTheft.risk}
                 </div>

                 <div
                   style={{
                     marginBottom: "14px",
                     fontWeight: "700",
                   }}
                 >
                   Recommended:
                   {" "}
                   {identityTheft.recommended}
                 </div>

                 <div
                   style={{
                     fontWeight: "700",
                     marginBottom: "8px",
                   }}
                 >
                   Next Step
                 </div>

                 <div
                   style={{
                     color: "#475569",
                     marginBottom: "20px",
                   }}
                 >
                   {identityTheft.nextStep}
                 </div>

                 <div
                   style={{
                     fontWeight: "700",
                     marginBottom: "8px",
                   }}
                 >
                   Applicable Laws
                 </div>

                 {identityTheft.laws.map((law, index) => (
                   <div
                     key={index}
                     style={{
                       color: "#D4AF37",
                       marginBottom: "6px",
                     }}
                   >
                     📚 {law}
                   </div>
                 ))}

                 <div
                   style={{
                     fontWeight: "700",
                     marginTop: "20px",
                     marginBottom: "8px",
                   }}
                 >
                   Required Documents
                 </div>

                 {identityTheft.documents.map((doc, index) => (
                   <div
                     key={index}
                     style={{
                       color: "#475569",
                       marginBottom: "6px",
                     }}
                   >
                     📂 {doc}
                   </div>
                 ))}
               </div>

               <hr
                 style={{
                   marginTop: "20px",
                   marginBottom: "20px",
                 }}
               />

               <div
                 style={{
                   color: "#D4AF37",
                   fontSize: "20px",
                   fontWeight: "800",
                   marginBottom: "20px",
                 }}
               >
                 FTC Affidavit Engine
               </div>

               <div
                 style={{
                   marginBottom: "12px",
                 }}
               >
                 Needed: {ftc.needed}
               </div>

               <div
                 style={{
                   marginBottom: "12px",
                 }}
               >
                 Strength: {ftc.strength}
               </div>

               <div
                 style={{
                   fontWeight: "700",
                   marginTop: "20px",
                   marginBottom: "10px",
                 }}
               >
                 Applicable Laws
               </div>

               {ftc.laws.map((law, index) => (
                 <div key={index}>
                   📚 {law}
                 </div>
               ))}

               <div
                 style={{
                   fontWeight: "700",
                   marginTop: "20px",
                   marginBottom: "10px",
                 }}
               >
                 Required Documents
               </div>
 
               {ftc.documents.map((doc, index) => (
                 <div key={index}>
                   📂 {doc}
                 </div>
               ))}

               <div
                 style={{
                   fontWeight: "700",
                   marginTop: "20px",
                   marginBottom: "10px",
                 }}
               >
                 Next Step
               </div>

               <div
                 style={{
                   color: "#475569",
                 }}
               >
                 {ftc.nextStep}
               </div>

               <hr
                 style={{
                   marginTop: "20px",
                   marginBottom: "20px",
                 }}
               />

               <div
                 style={{
                   color: "#16a34a",
                   fontSize: "20px",
                   fontWeight: "800",
                   marginBottom: "20px",
                 }}
               >
                 CFPB Complaint Escalation
               </div>

               <div
                 style={{
                 marginBottom: "12px",
               }}
             >
               Urgency: {cfpbEscalation.urgency}
             </div>

             <div
               style={{
                 marginBottom: "12px",
               }}
             >
               Complaint Type: {cfpbEscalation.complaintType}
             </div>

             <div
               style={{
                 fontWeight: "700",
                 marginBottom: "10px",
               }}
             >
               Recommended Attachments
             </div>

             {cfpbEscalation.attachments.map((doc, index) => (
               <div key={index}>
                 📂 {doc}
               </div>
             ))}

             <div
               style={{
                 fontWeight: "700",
                 marginTop: "20px",
                 marginBottom: "10px",
               }}
             >
               Strategy
             </div>

             <div
               style={{
                 color: "#475569",
               }}
             >
               {cfpbEscalation.strategy}
             </div>

             <hr style={{ margin: "40px 0" }} />

             <div
               style={{
                 color: "#D4AF37",
                 fontSize: "24px",
                 fontWeight: "800",
                 marginBottom: "20px",
               }}
             >
               Furnisher Direct Dispute
             </div>

             <div style={{ marginBottom: "14px" }}>
               Recommended: {furnisherDispute.recommended}
             </div>

             <div style={{ marginBottom: "14px" }}>
               Target:
               {" "}
               {furnisherDispute.target}
             </div>

             <div
               style={{
                 fontWeight: "700",
                 marginBottom: "8px",
               }}
             >
               Applicable Laws
             </div>

             <div style={{ color: "#475569" }}>
               {furnisherDispute.laws.map((law) => (
                 <div key={law}>📚 {law}</div>
               ))}
             </div>

             <div
               style={{
                 fontWeight: "700",
                 marginTop: "18px",
                 marginBottom: "8px",
               }}
             >
               Evidence Needed
             </div>

             <div style={{ color: "#475569" }}>
               {furnisherDispute.evidence.map((item) => (
             <div key={item}>📂 {item}</div>
           ))}
         </div>

         <div
           style={{
             fontWeight: "700",
             marginTop: "18px",
             marginBottom: "8px",
           }}
         >
           Strategy
         </div>

         <div style={{ color: "#475569" }}>
           {furnisherDispute.strategy}
         </div>

         <div
           style={{
             background: "#FFF8E1",
             border: "2px solid #D4AF37",
             borderRadius: "16px",
             padding: "25px",
             marginBottom: "30px",
           }}
         >
           <div
             style={{
               fontSize: "28px",
               fontWeight: "800",
               color: "#B8860B",
               marginBottom: "20px",
             }}
           >
             🤖 AI Recommended Action Plan
           </div>

           <div style={{ marginBottom: "12px" }}>
             Priority: {masterRecommendation.priority}
           </div>

           <div style={{ marginBottom: "12px" }}>
             Primary Violation: {masterRecommendation.primaryViolation}
           </div>

           <div style={{ marginBottom: "12px" }}>
             Best Letter: {masterRecommendation.bestLetter}
           </div>

           <div style={{ marginBottom: "12px" }}>
             Recommended Letter Type: {smartLetterRouting.letterType}
           </div>

           <div style={{ marginBottom: "12px" }}>
             Routing Reason: {smartLetterRouting.reason}
           </div>

           <div style={{ marginBottom: "12px" }}>
             CFPB Ready: {masterRecommendation.cfpbReady}
           </div>

           <div style={{ marginBottom: "12px" }}>
             Attorney Demand Ready: {masterRecommendation.attorneyDemandReady}
           </div>

           <div
             style={{
               fontWeight: "700",
               marginTop: "20px",
               marginBottom: "10px",
             }}
           >
             Evidence Needed
           </div>

           <div
             style={{
               color: "#475569",
               marginBottom: "20px",
             }}
           >
             {evidenceChecklist.evidence.map((item) => (
               <div key={item}>📂 {item}</div>
             ))}
           </div>

           <div
             style={{
               fontWeight: "700",
               marginTop: "20px",
               marginBottom: "10px",
             }}
           >
             Recommendation
           </div>

           <div style={{ color: "#475569" }}>
             {masterRecommendation.recommendation}
           </div>
         </div>

         <hr style={{ margin: "30px 0" }} />

         <div
           style={{
             fontSize: "24px",
             fontWeight: "800",
             color: "#D4AF37",
             marginBottom: "20px",
           }}
         >
           📁 Case Builder
         </div>

         <div style={{ marginBottom: "12px" }}>
           Case Status: {caseBuilder.caseStatus}
         </div>

         <div style={{ marginBottom: "12px" }}>
           Priority: {caseBuilder.priority}
         </div>

         <div style={{ marginBottom: "12px" }}>
           Recommended Letter: {caseBuilder.recommendedLetter}
         </div>

         <div style={{ marginBottom: "12px" }}>
           Escalation: {caseBuilder.escalation}
         </div>

         <div style={{ marginBottom: "12px" }}>
           Attorney Demand Ready: {caseBuilder.attorneyDemand}
         </div>

         <div
           style={{
             fontWeight: "700",
             marginTop: "20px",
             marginBottom: "10px",
           }}
         >
           Next Step
         </div>

         <div style={{ color: "#475569" }}>
           {caseBuilder.nextStep}
         </div>

         <hr style={{ margin: "30px 0" }} />

         <div
           style={{
      fontSize: "24px",
      fontWeight: "800",
      color: "#dc2626",
      marginBottom: "20px",
    }}
  >
    📬 CFPB Packet
  </div>

  <div style={{ marginBottom: "12px" }}>
    Escalation: {cfpbPacket.escalation}
  </div>

  <div
    style={{
      fontWeight: "700",
      marginTop: "20px",
      marginBottom: "10px",
    }}
  >
    Complaint Summary
  </div>

  <div style={{ color: "#475569" }}>
    {cfpbPacket.complaintSummary}
  </div>

  <div
    style={{
      fontWeight: "700",
      marginTop: "20px",
      marginBottom: "10px",
    }}
  >
    Violations
  </div>

  <div style={{ color: "#475569" }}>
    {cfpbPacket.violations.map((item) => (
      <div key={item}>⚠️ {item}</div>
    ))}
  </div>

  <div
    style={{
      fontWeight: "700",
      marginTop: "20px",
      marginBottom: "10px",
    }}
  >
    Evidence
  </div>

  <div style={{ color: "#475569" }}>
    {cfpbPacket.evidence.map((item) => (
      <div key={item}>📂 {item}</div>
    ))}
  </div>

  <div
    style={{
      fontWeight: "700",
      marginTop: "20px",
      marginBottom: "10px",
    }}
  >
    Requested Resolution
  </div>

  <div style={{ color: "#475569" }}>
    {cfpbPacket.requestedResolution}
  </div>

         <div
           style={{
             fontWeight: "700",
             marginTop: "20px",
             marginBottom: "10px",
           }}
         >
           Evidence Needed
         </div>

         <div style={{ color: "#475569" }}>
           {caseBuilder.evidenceNeeded.map((item) => (
             <div key={item}>📂 {item}</div>
           ))}
         </div>

         <button
           type="button"
           onClick={async () => {
             try {
               const data = await createCreditCase({
                   client_id: selectedClientId || account.client_id,
                   account_name:
                     account.creditor ||
                     account.creditor_name ||
                     account.account_name ||
                     "Unknown Account",
                   account_type: account.account_type || "",
                   bureau: account.bureau || "",

                   priority: caseBuilder.priority,
                   primary_violation: masterRecommendation.primaryViolation,
                   recommended_letter: caseBuilder.recommendedLetter,
                   escalation: caseBuilder.escalation,
                   attorney_demand_ready: caseBuilder.attorneyDemand,
                   case_status: caseBuilder.caseStatus,
                   next_step: caseBuilder.nextStep,

                   evidence_needed: caseBuilder.evidenceNeeded,
                   cfpb_summary: cfpbPacket.complaintSummary,

                   full_analysis: {
                     masterRecommendation,
                     evidenceChecklist,
                     smartLetterRouting,
                     caseBuilder,
                     cfpbPacket,
                   },
               });

               alert(`Case saved successfully. Case ID: ${data.case_id}`);
             } catch (err) {
               alert(err.message || "Failed to save case");
             }
           }}
           style={{
             marginTop: "20px",
             padding: "12px 18px",
             borderRadius: "12px",
             border: "none",
             background: "#16a34a",
             color: "white",
             fontWeight: "700",
             cursor: "pointer",
           }}
         >
           💾 Save Case File
         </button>

         <hr style={{ margin: "40px 0" }} />

         <div
           style={{
             color: "#D4AF37",
             fontSize: "24px",
             fontWeight: "800",
             marginBottom: "20px",
           }}
         >
           Metro 2 Compliance Analysis
         </div>

         <div style={{ marginBottom: "14px" }}>
           Strength: {metro2Compliance.strength}
         </div>

         <div
           style={{
             fontWeight: "700",
             marginBottom: "8px",
           }}
         >
           Potential Violations
         </div>

         <div style={{ color: "#475569" }}>
           {metro2Compliance.violations.map((item) => (
             <div key={item}>⚖️ {item}</div>
           ))}
         </div>

         <div
           style={{
             fontWeight: "700",
             marginTop: "18px",
             marginBottom: "8px",
           }}
          >
           Applicable Laws
         </div>

         <div style={{ color: "#475569" }}>
           {metro2Compliance.laws.map((law) => (
             <div key={law}>📚 {law}</div>
           ))}
         </div>

         <div
           style={{
             fontWeight: "700",
             marginTop: "18px",
             marginBottom: "8px",
           }}
         >
           Recommendation
         </div>

         <div style={{ color: "#475569" }}>
           {metro2Compliance.recommendation}
         </div>
        
         <hr style={{ margin: "40px 0" }} />

         <div
           style={{
             color: "#dc2626",
             fontSize: "24px",
             fontWeight: "800",
             marginBottom: "20px",
           }}
         >
           Duplicate Account Detection
         </div>

        <div style={{ marginBottom: "14px" }}>
          Risk: {duplicateAccount.risk}
        </div>

        <div style={{ marginBottom: "14px" }}>
          Status: {duplicateAccount.status}
        </div>

        <div
          style={{
            fontWeight: "700",
            marginBottom: "8px",
          }}
        >
          Red Flags
        </div>

        <div style={{ color: "#475569" }}>
          {duplicateAccount.redFlags.map((flag) => (
            <div key={flag}>⚠️ {flag}</div>
          ))}
        </div>

        <div
          style={{
            fontWeight: "700",
            marginTop: "18px",
            marginBottom: "8px",
          }} 
        > 
          Applicable Laws
        </div>

        <div style={{ color: "#475569" }}>
          {duplicateAccount.laws.map((law) => (
            <div key={law}>📚 {law}</div>
          ))}
        </div>

        <div
          style={{
            fontWeight: "700",
            marginTop: "18px",
            marginBottom: "8px",
          }}
        >
          Recommendation
        </div>

        <div style={{ color: "#475569" }}>
          {duplicateAccount.recommendation}
        </div>

        <hr style={{ margin: "40px 0" }} />

        <div
          style={{
            color: "#dc2626",
            fontSize: "24px",
            fontWeight: "800",
            marginBottom: "20px",
          }}
        >
          Re-Aging Detection
        </div>

        <div style={{ marginBottom: "14px" }}>
          Risk: {reAging.risk}
        </div>

        <div style={{ marginBottom: "14px" }}>
          Status: {reAging.suspected}
        </div>

        <div
          style={{
            fontWeight: "700",
            marginBottom: "8px",
          }}
        >
          Red Flags
        </div>

        <div style={{ color: "#475569" }}>
          {reAging.redFlags.map((flag) => (
            <div key={flag}>⚠️ {flag}</div>
          ))}
        </div>

        <div
          style={{
            fontWeight: "700",
            marginTop: "18px",
            marginBottom: "8px",
          }}
        >
          Applicable Laws
        </div>

        <div style={{ color: "#475569" }}>
          {reAging.laws.map((law) => (
            <div key={law}>📚 {law}</div>
          ))}
        </div>

        <div
          style={{
            fontWeight: "700",
            marginTop: "18px",
            marginBottom: "8px",
          }}
        >
          Recommendation
        </div>

        <div style={{ color: "#475569" }}>
          {reAging.recommendation}
        </div>

        <hr style={{ margin: "40px 0" }} />

        <div
          style={{
            color: "#dc2626",
            fontSize: "24px",
            fontWeight: "800",
            marginBottom: "20px",
          }}
        >
          Zombie Debt Detection
        </div>

        <div style={{ marginBottom: "14px" }}>
          Risk: {zombieDebt.risk}
        </div>

        <div style={{ marginBottom: "14px" }}>
          Status: {zombieDebt.status}
        </div>

        <div
          style={{
            fontWeight: "700",
            marginBottom: "8px",
          }}
        >
          Warning Signs
        </div>

        <div style={{ color: "#475569" }}>
          {zombieDebt.redFlags.map((flag) => (
            <div key={flag}>⚠️ {flag}</div>
          ))}
        </div>

        <div
          style={{
            fontWeight: "700",
            marginTop: "18px",
            marginBottom: "8px",
          }}
        >
          Applicable Laws
        </div>

        <div style={{ color: "#475569" }}>
          {zombieDebt.laws.map((law) => (
            <div key={law}>📚 {law}</div>
          ))}
        </div>

        <div
          style={{
            fontWeight: "700",
            marginTop: "18px",
            marginBottom: "8px",
          }}
        >
          Recommendation
        </div>

        <div style={{ color: "#475569" }}>
          {zombieDebt.recommendation}
        </div>    
    
        <hr style={{ margin: "40px 0" }} />

        <div
          style={{
            color: "#dc2626",
            fontSize: "24px",
            fontWeight: "800",
            marginBottom: "20px",
          }}
        >
          Bankruptcy Reporting Violations
        </div>

       <div style={{ marginBottom: "14px" }}>
         Risk: {bankruptcyViolation.risk}
       </div>

       <div style={{ marginBottom: "14px" }}>
         Status: {bankruptcyViolation.status}
       </div>

       <div
         style={{
           fontWeight: "700",
           marginBottom: "8px",
         }}
       >
         Possible Violations
       </div>

       <div style={{ color: "#475569" }}>
         {bankruptcyViolation.violations.map((item) => (
           <div key={item}>⚠️ {item}</div>
         ))}
       </div>

       <div
         style={{
           fontWeight: "700",
           marginTop: "18px",
           marginBottom: "8px",
         }}
       >
         Applicable Laws
       </div>

       <div style={{ color: "#475569" }}>
         {bankruptcyViolation.laws.map((law) => (
           <div key={law}>📚 {law}</div>
         ))}
       </div>

       <div
         style={{
           fontWeight: "700",
           marginTop: "18px",
           marginBottom: "8px",
         }}
       >
         Recommendation
       </div>

      <div style={{ color: "#475569" }}>
        {bankruptcyViolation.recommendation}
      </div>        

      <hr style={{ margin: "40px 0" }} />

      <div
        style={{
          color: "#dc2626",
          fontSize: "24px",
          fontWeight: "800",
          marginBottom: "20px",
        }}
      >
        Charge-Off Accuracy Analysis
      </div>

      <div style={{ marginBottom: "14px" }}>
        Risk: {chargeOffAccuracy.risk}
      </div>

      <div style={{ marginBottom: "14px" }}>
        Status: {chargeOffAccuracy.status}
      </div>

      <div
        style={{
          fontWeight: "700",
          marginBottom: "8px",
        }}
      >
        Possible Violations
      </div>

      <div style={{ color: "#475569" }}>
        {chargeOffAccuracy.violations.map((item) => (
          <div key={item}>⚠️ {item}</div>
        ))}
      </div>

      <div
        style={{
          fontWeight: "700",
          marginTop: "18px",
          marginBottom: "8px",
        }}
      >
        Applicable Laws
      </div>

     <div style={{ color: "#475569" }}>
       {chargeOffAccuracy.laws.map((law) => (
         <div key={law}>📚 {law}</div>
       ))}
     </div>

     <div
       style={{
         fontWeight: "700",
         marginTop: "18px",
         marginBottom: "8px",
       }}
     >
       Recommendation
     </div>

     <div style={{ color: "#475569" }}>
       {chargeOffAccuracy.recommendation}
     </div>      

     <hr style={{ margin: "40px 0" }} />

     <div
       style={{
         color: "#dc2626",
         fontSize: "24px",
         fontWeight: "800",
         marginBottom: "20px",
       }}
     >
       Collection Agency Authority Analysis
     </div>

     <div style={{ marginBottom: "14px" }}>
       Risk: {collectionAuthority.risk}
     </div>

     <div style={{ marginBottom: "14px" }}>
       Status: {collectionAuthority.status}
     </div>

     <div
       style={{
         fontWeight: "700",
         marginBottom: "8px",
       }}
     >
       Possible Violations
     </div>

     <div style={{ color: "#475569" }}>
       {collectionAuthority.violations.map((item) => (
         <div key={item}>⚠️ {item}</div>
       ))}
     </div>

     <div
       style={{
         fontWeight: "700",
         marginTop: "18px",
         marginBottom: "8px",
       }}
     >
       Applicable Laws
     </div>

     <div style={{ color: "#475569" }}>
       {collectionAuthority.laws.map((law) => (
         <div key={law}>📚 {law}</div>
       ))}
     </div>

     <div
       style={{
         fontWeight: "700",
         marginTop: "18px",
         marginBottom: "8px",
       }}
     >
       Recommendation
     </div>

     <div style={{ color: "#475569" }}>
       {collectionAuthority.recommendation}
     </div>

     <hr style={{ margin: "40px 0" }} />

     <div
       style={{
         color: "#dc2626",
         fontSize: "24px",
         fontWeight: "800",
         marginBottom: "20px",
       }}
     >
       Mixed File / Identity Contamination Analysis
     </div>

     <div style={{ marginBottom: "14px" }}>
       Risk: {mixedFile.risk}
     </div>

     <div style={{ marginBottom: "14px" }}>
       Status: {mixedFile.status}
     </div>
 
     <div
       style={{
         fontWeight: "700",
         marginBottom: "8px",
       }}
     >
       Possible Violations
     </div>

     <div style={{ color: "#475569" }}>
       {mixedFile.violations.map((item) => (
         <div key={item}>⚠️ {item}</div>
       ))}
     </div>

     <div
       style={{
         fontWeight: "700",
         marginTop: "18px",
         marginBottom: "8px",
       }}
     >
       Applicable Laws
     </div>

     <div style={{ color: "#475569" }}>
       {mixedFile.laws.map((law) => (
         <div key={law}>📚 {law}</div>
       ))}
     </div>

     <div
       style={{
         fontWeight: "700",
         marginTop: "18px",
         marginBottom: "8px",
       }}
     >
       Recommendation
     </div>

     <div style={{ color: "#475569" }}>
       {mixedFile.recommendation}
     </div>      

     <hr style={{ margin: "40px 0" }} />

     <div
       style={{
         color: "#dc2626",
         fontSize: "24px",
         fontWeight: "800",
         marginBottom: "20px",
       }}
     >
       Secondary Reporting Agency Analysis
     </div>

     <div style={{ marginBottom: "14px" }}>
      Risk: {secondaryBureau.risk}
    </div>

    <div style={{ marginBottom: "14px" }}>
      Status: {secondaryBureau.status}
    </div>

    <div
      style={{
        fontWeight: "700",
        marginBottom: "8px",
      }}
    >
      Possible Violations
    </div>

    <div style={{ color: "#475569" }}>
      {secondaryBureau.violations.map((item) => (
        <div key={item}>⚠️ {item}</div>
      ))}
    </div>

    <div
      style={{
        fontWeight: "700",
        marginTop: "18px",
        marginBottom: "8px",
      }}
    >
      Applicable Laws
    </div>

    <div style={{ color: "#475569" }}>
      {secondaryBureau.laws.map((law) => (
        <div key={law}>📚 {law}</div>
      ))}
    </div>

    <div
      style={{
        fontWeight: "700",
        marginTop: "18px",
        marginBottom: "8px",
      }}
    >
      Recommendation
    </div>

    <div style={{ color: "#475569" }}>
      {secondaryBureau.recommendation}
    </div>
      
    <hr style={{ margin: "40px 0" }} />

    <div
      style={{
        color: "#dc2626",
        fontSize: "24px",
        fontWeight: "800",
        marginBottom: "20px",
      }}
    >
      Inquiry Permissible Purpose Analysis
    </div>

    <div style={{ marginBottom: "14px" }}>
      Risk: {inquiryPermissiblePurpose.risk}
    </div>

    <div style={{ marginBottom: "14px" }}>
      Status: {inquiryPermissiblePurpose.status}
    </div>

    <div
      style={{
        fontWeight: "700",
        marginBottom: "8px",
      }}
    >
      Possible Violations
    </div>

    <div style={{ color: "#475569" }}>
      {inquiryPermissiblePurpose.violations.map((item) => (
        <div key={item}>⚠️ {item}</div>
      ))}
    </div>

    <div
      style={{
        fontWeight: "700",
        marginTop: "18px",
        marginBottom: "8px",
      }}
    >
      Applicable Laws
    </div>

    <div style={{ color: "#475569" }}>
      {inquiryPermissiblePurpose.laws.map((law) => (
        <div key={law}>📚 {law}</div>
      ))}
    </div>

    <div
      style={{
        fontWeight: "700",
        marginTop: "18px",
        marginBottom: "8px",
      }}
    >
      Recommendation
    </div>

    <div style={{ color: "#475569" }}>
      {inquiryPermissiblePurpose.recommendation}
    </div>              

    <hr style={{ margin: "40px 0" }} />

    <div
      style={{
        color: "#dc2626",
        fontSize: "24px",
        fontWeight: "800",
        marginBottom: "20px",
      }}
    >
      Date of First Delinquency (DOFD) Analysis
    </div>

    <div style={{ marginBottom: "14px" }}>
      Risk: {dofdViolation.risk}
    </div>

    <div style={{ marginBottom: "14px" }}>
      Status: {dofdViolation.status}
    </div>

    <div
      style={{
        fontWeight: "700",
        marginBottom: "8px",
      }}
    >
      Possible Violations
    </div>

    <div style={{ color: "#475569" }}>
      {dofdViolation.violations.map((item) => (
        <div key={item}>⚠️ {item}</div>
      ))}
    </div>

    <div
      style={{
        fontWeight: "700",
        marginTop: "18px",
        marginBottom: "8px",
      }}
    >
      Applicable Laws
    </div>

    <div style={{ color: "#475569" }}>
      {dofdViolation.laws.map((law) => (
        <div key={law}>📚 {law}</div>
      ))}
    </div>

    <div
      style={{
        fontWeight: "700",
        marginTop: "18px",
        marginBottom: "8px",
      }}
    >
      Recommendation
    </div>

    <div style={{ color: "#475569" }}>
      {dofdViolation.recommendation}
    </div>

    <hr style={{ margin: "40px 0" }} />

    <div
      style={{
        color: "#dc2626",
        fontSize: "24px",
        fontWeight: "800",
        marginBottom: "20px",
      }}
    >
      Payment History Consistency Analysis
    </div>

    <div style={{ marginBottom: "14px" }}>
      Risk: {paymentHistoryConsistency.risk}
    </div>

    <div style={{ marginBottom: "14px" }}>
      Status: {paymentHistoryConsistency.status}
    </div>

    <div
      style={{
        fontWeight: "700",
        marginBottom: "8px",
      }}
    >
      Possible Violations
    </div>

    <div style={{ color: "#475569" }}>
      {paymentHistoryConsistency.violations.map((item) => (
        <div key={item}>⚠️ {item}</div>
      ))}
    </div>

    <div
      style={{
        fontWeight: "700",
        marginTop: "18px",
        marginBottom: "8px",
      }}
    >
      Applicable Laws
    </div>

    <div style={{ color: "#475569" }}>
      {paymentHistoryConsistency.laws.map((law) => (
        <div key={law}>📚 {law}</div>
      ))}
    </div>

    <div
      style={{
        fontWeight: "700",
        marginTop: "18px",
        marginBottom: "8px",
      }}
    >
      Recommendation
    </div>

    <div style={{ color: "#475569" }}>
      {paymentHistoryConsistency.recommendation}
    </div>

    <hr style={{ margin: "40px 0" }} />

    <div
      style={{
        color: "#dc2626",
        fontSize: "24px",
        fontWeight: "800",
        marginBottom: "20px",
      }}
    >
      Utilization Manipulation Analysis
    </div>

    <div style={{ marginBottom: "14px" }}>
      Risk: {utilizationManipulation.risk}
    </div>

    <div style={{ marginBottom: "14px" }}>
      Status: {utilizationManipulation.status}
    </div>

    <div style={{ marginBottom: "14px" }}>
      Utilization: {utilizationManipulation.utilization}%
    </div>

    <div
      style={{
        fontWeight: "700",
        marginBottom: "8px",
      }}
    >
      Possible Violations
    </div>

    <div style={{ color: "#475569" }}>
      {utilizationManipulation.violations.map((item) => (
        <div key={item}>⚠️ {item}</div>
      ))}
    </div>

    <div
      style={{
        fontWeight: "700",
        marginTop: "18px",
        marginBottom: "8px",
      }}
    >
      Applicable Laws
    </div>

    <div style={{ color: "#475569" }}>
      {utilizationManipulation.laws.map((law) => (
        <div key={law}>📚 {law}</div>
      ))}
    </div>

    <div
      style={{
        fontWeight: "700",
        marginTop: "18px",
        marginBottom: "8px",
      }}
    >
      Recommendation
    </div>

    <div style={{ color: "#475569" }}>
      {utilizationManipulation.recommendation}
    </div>

    <hr style={{ margin: "40px 0" }} />

    <div
      style={{
        color: "#dc2626",
        fontSize: "24px",
        fontWeight: "800",
        marginBottom: "20px",
      }}
    >
      Creditor Name Consistency Analysis
    </div>

    <div style={{ marginBottom: "14px" }}>
      Risk: {creditorNameConsistency.risk}
    </div>

    <div style={{ marginBottom: "14px" }}>
      Status: {creditorNameConsistency.status}
    </div>

    <div
      style={{
        fontWeight: "700",
        marginBottom: "8px",
      }}
    >
      Possible Violations
    </div>

    <div style={{ color: "#475569" }}>
      {creditorNameConsistency.violations.map((item) => (
        <div key={item}>⚠️ {item}</div>
      ))}
    </div>

    <div
      style={{
        fontWeight: "700",
        marginTop: "18px",
        marginBottom: "8px",
      }}
    >
      Applicable Laws
    </div>

    <div style={{ color: "#475569" }}>
      {creditorNameConsistency.laws.map((law) => (
        <div key={law}>📚 {law}</div>
      ))}
    </div>

    <div
      style={{
        fontWeight: "700",
        marginTop: "18px",
        marginBottom: "8px",
      }}
    >
      Recommendation
    </div>

    <div style={{ color: "#475569" }}>
      {creditorNameConsistency.recommendation}
    </div>

    <hr style={{ margin: "40px 0" }} />

    <div
      style={{
        color: "#dc2626",
        fontSize: "24px",
        fontWeight: "800",
        marginBottom: "20px",
      }}
    >
      Account Status Contradiction Analysis
    </div>

    <div style={{ marginBottom: "14px" }}>
      Risk: {accountStatusContradiction.risk}
    </div>

    <div style={{ marginBottom: "14px" }}>
      Status: {accountStatusContradiction.status}
    </div>

    <div style={{ fontWeight: "700", marginBottom: "8px" }}>
      Possible Violations
    </div>

    <div style={{ color: "#475569" }}>
      {accountStatusContradiction.violations.map((item) => (
        <div key={item}>⚠️ {item}</div>
      ))}
    </div>

    <div style={{ fontWeight: "700", marginTop: "18px", marginBottom: "8px" }}>
      Applicable Laws
    </div>

    <div style={{ color: "#475569" }}>
      {accountStatusContradiction.laws.map((law) => (
        <div key={law}>📚 {law}</div>
      ))}
    </div>

    <div style={{ fontWeight: "700", marginTop: "18px", marginBottom: "8px" }}>
      Recommendation
    </div>

    <div style={{ color: "#475569" }}>
      {accountStatusContradiction.recommendation}
    </div>

    <hr style={{ margin: "40px 0" }} />

    <div
      style={{
        color: "#dc2626",
        fontSize: "24px",
        fontWeight: "800",
        marginBottom: "20px",
      }}
    >
      Balance Accuracy Analysis
    </div>

    <div style={{ marginBottom: "14px" }}>
      Risk: {balanceAccuracy.risk}
    </div>

    <div style={{ marginBottom: "14px" }}>
      Status: {balanceAccuracy.status}
    </div>

    <div
      style={{
        fontWeight: "700",
        marginBottom: "8px",
      }}
    >
      Possible Violations
    </div>

    <div style={{ color: "#475569" }}>
      {balanceAccuracy.violations.map((item) => (
        <div key={item}>⚠️ {item}</div>
      ))}
    </div>

    <div
      style={{
        fontWeight: "700",
        marginTop: "18px",
        marginBottom: "8px",
      }}
    >
      Applicable Laws
    </div>

    <div style={{ color: "#475569" }}>
      {balanceAccuracy.laws.map((law) => (
        <div key={law}>📚 {law}</div>
      ))}
    </div>

    <div
      style={{
        fontWeight: "700",
        marginTop: "18px",
        marginBottom: "8px",
      }}
    >
      Recommendation
    </div>

    <div style={{ color: "#475569" }}>
      {balanceAccuracy.recommendation}
    </div>

    <hr style={{ margin: "40px 0" }} />

    <div
      style={{
        color: "#dc2626",
        fontSize: "24px",
        fontWeight: "800",
        marginBottom: "20px",
      }}
    >
      Date Last Reported Manipulation Analysis
    </div>

    <div style={{ marginBottom: "14px" }}>
      Risk: {dateLastReported.risk}
    </div>

    <div style={{ marginBottom: "14px" }}>
      Status: {dateLastReported.status}
    </div>

    <div
      style={{
        fontWeight: "700",
        marginBottom: "8px",
      }}
    >
      Possible Violations
    </div>

    <div style={{ color: "#475569" }}>
      {dateLastReported.violations.map((item) => (
        <div key={item}>⚠️ {item}</div>
      ))}
    </div>

    <div
      style={{
        fontWeight: "700",
        marginTop: "18px",
        marginBottom: "8px",
      }}
    >
      Applicable Laws
    </div>

    <div style={{ color: "#475569" }}>
      {dateLastReported.laws.map((law) => (
        <div key={law}>📚 {law}</div>
      ))}
    </div>

    <div
      style={{
        fontWeight: "700",
        marginTop: "18px",
        marginBottom: "8px",
      }}
    >
      Recommendation
    </div>

    <div style={{ color: "#475569" }}>
      {dateLastReported.recommendation}
    </div>

    <hr style={{ margin: "40px 0" }} />

    <div
      style={{
        color: "#dc2626",
        fontSize: "24px",
        fontWeight: "800",
        marginBottom: "20px",
      }}
    >
      High Utilization Risk Analysis
    </div>

    <div style={{ marginBottom: "14px" }}>
      Risk: {highUtilizationRisk.risk}
    </div>

    <div style={{ marginBottom: "14px" }}>
      Status: {highUtilizationRisk.status}
    </div>

    <div style={{ marginBottom: "14px" }}>
      Utilization: {highUtilizationRisk.utilization}%
    </div>

    <div
      style={{
        fontWeight: "700",
        marginBottom: "8px",
      }}
    >
      Possible Violations
    </div>

    <div style={{ color: "#475569" }}>
      {highUtilizationRisk.violations.map((item) => (
        <div key={item}>⚠️ {item}</div>
      ))}
    </div>

    <div
      style={{
      fontWeight: "700",
      marginTop: "18px",
      marginBottom: "8px",
    }}
  >
    Applicable Laws
  </div>

  <div style={{ color: "#475569" }}>
    {highUtilizationRisk.laws.map((law) => (
      <div key={law}>📚 {law}</div>
    ))}
  </div>

  <div
    style={{
      fontWeight: "700",
      marginTop: "18px",
      marginBottom: "8px",
    }}
  >
    Recommendation
  </div>

  <div style={{ color: "#475569" }}>
    {highUtilizationRisk.recommendation}
  </div>

  <hr style={{ margin: "40px 0" }} />

  <div
    style={{
      color: "#dc2626",
      fontSize: "24px",
      fontWeight: "800",
      marginBottom: "20px",
    }}
  >
    Account Age Manipulation Analysis
  </div>

  <div style={{ marginBottom: "14px" }}>
    Risk: {accountAgeManipulation.risk}
  </div>

  <div style={{ marginBottom: "14px" }}>
    Status: {accountAgeManipulation.status}
  </div>

  <div
    style={{
      fontWeight: "700",
      marginBottom: "8px",
    }}
  >
    Possible Violations
  </div>

  <div style={{ color: "#475569" }}>
    {accountAgeManipulation.violations.map((item) => (
      <div key={item}>⚠️ {item}</div>
    ))}
  </div>

  <div
    style={{
      fontWeight: "700",
      marginTop: "18px",
      marginBottom: "8px",
    }}
  >
    Applicable Laws
  </div>

  <div style={{ color: "#475569" }}>
    {accountAgeManipulation.laws.map((law) => (
      <div key={law}>📚 {law}</div>
    ))}
  </div>

  <div
    style={{
      fontWeight: "700",
      marginTop: "18px",
      marginBottom: "8px",
    }}
  >
    Recommendation
  </div>

  <div style={{ color: "#475569" }}>
    {accountAgeManipulation.recommendation}
  </div>

  <hr style={{ margin: "40px 0" }} />

  <div
    style={{
      color: "#dc2626",
      fontSize: "24px",
      fontWeight: "800",
      marginBottom: "20px",
    }}
  >
    Credit Limit Accuracy Analysis
  </div>

  <div style={{ marginBottom: "14px" }}>
    Risk: {creditLimitAccuracy.risk}
  </div>

  <div style={{ marginBottom: "14px" }}>
    Status: {creditLimitAccuracy.status}
  </div>

  <div
    style={{
      fontWeight: "700",
      marginBottom: "8px",
    }}
  >
    Possible Violations
  </div>

  <div style={{ color: "#475569" }}>
    {creditLimitAccuracy.violations.map((item) => (
      <div key={item}>⚠️ {item}</div>
    ))}
  </div>

  <div
    style={{
      fontWeight: "700",
      marginTop: "18px",
      marginBottom: "8px",
    }}
  >
    Applicable Laws
  </div>

  <div style={{ color: "#475569" }}>
    {creditLimitAccuracy.laws.map((law) => (
      <div key={law}>📚 {law}</div>
    ))}
  </div>

  <div
    style={{
      fontWeight: "700",
      marginTop: "18px",
      marginBottom: "8px",
    }}
  >
    Recommendation
  </div>

  <div style={{ color: "#475569" }}>
    {creditLimitAccuracy.recommendation}
  </div>

  <hr style={{ margin: "40px 0" }} />

  <div
    style={{
      color: "#dc2626",
      fontSize: "24px",
      fontWeight: "800",
      marginBottom: "20px",
    }}
  >
    Monthly Payment Accuracy Analysis
  </div>

  <div style={{ marginBottom: "14px" }}>
    Risk: {monthlyPaymentAccuracy.risk}
  </div>

  <div style={{ marginBottom: "14px" }}>
    Status: {monthlyPaymentAccuracy.status}
  </div>

  <div
    style={{
      fontWeight: "700",
      marginBottom: "8px",
    }}
  >
    Possible Violations
  </div>

  <div style={{ color: "#475569" }}>
    {monthlyPaymentAccuracy.violations.map((item) => (
      <div key={item}>⚠️ {item}</div>
    ))}
  </div>

  <div
    style={{
      fontWeight: "700",
      marginTop: "18px",
      marginBottom: "8px",
    }}
  >
    Applicable Laws
  </div>

  <div style={{ color: "#475569" }}>
    {monthlyPaymentAccuracy.laws.map((law) => (
      <div key={law}>📚 {law}</div>
    ))}
  </div>

  <div
    style={{
      fontWeight: "700",
      marginTop: "18px",
      marginBottom: "8px",
    }}
  >
    Recommendation
  </div>

  <div style={{ color: "#475569" }}>
    {monthlyPaymentAccuracy.recommendation}
  </div>

  <hr style={{ margin: "40px 0" }} />

  <div
    style={{
      color: "#dc2626",
      fontSize: "24px",
      fontWeight: "800",
      marginBottom: "20px",
    }}
  >
    Past Due Amount Accuracy Analysis
  </div>

  <div style={{ marginBottom: "14px" }}>
    Risk: {pastDueAmountAccuracy.risk}
  </div>

  <div style={{ marginBottom: "14px" }}>
    Status: {pastDueAmountAccuracy.status}
  </div>

  <div
    style={{
      fontWeight: "700",
      marginBottom: "8px",
    }}
  >
    Possible Violations
  </div>

  <div style={{ color: "#475569" }}>
    {pastDueAmountAccuracy.violations.map((item) => (
      <div key={item}>⚠️ {item}</div>
    ))}
  </div>

  <div
    style={{
      fontWeight: "700",
      marginTop: "18px",
      marginBottom: "8px",
    }}
  >
    Applicable Laws
  </div>

  <div style={{ color: "#475569" }}>
    {pastDueAmountAccuracy.laws.map((law) => (
      <div key={law}>📚 {law}</div>
    ))}
  </div>

  <div
    style={{
      fontWeight: "700",
      marginTop: "18px",
      marginBottom: "8px",
    }}
  >
    Recommendation
  </div>

  <div style={{ color: "#475569" }}>
    {pastDueAmountAccuracy.recommendation}
  </div>

  <hr style={{ margin: "40px 0" }} />

  <div
    style={{
      color: "#dc2626",
      fontSize: "24px",
      fontWeight: "800",
      marginBottom: "20px",
    }}
  >
    High Credit Amount Accuracy Analysis
  </div>

  <div style={{ marginBottom: "14px" }}>
    Risk: {highCreditAmountAccuracy.risk}
  </div>

  <div style={{ marginBottom: "14px" }}>
    Status: {highCreditAmountAccuracy.status}
  </div>

  <div style={{ fontWeight: "700", marginBottom: "8px" }}>
    Possible Violations
  </div>

  <div style={{ color: "#475569" }}>
    {highCreditAmountAccuracy.violations.map((item) => (
      <div key={item}>⚠️ {item}</div>
    ))}
  </div>

  <div style={{ fontWeight: "700", marginTop: "18px", marginBottom: "8px" }}>
    Applicable Laws
  </div>

  <div style={{ color: "#475569" }}>
    {highCreditAmountAccuracy.laws.map((law) => (
      <div key={law}>📚 {law}</div>
    ))}
  </div>

  <div style={{ fontWeight: "700", marginTop: "18px", marginBottom: "8px" }}>
    Recommendation
  </div>

  <div style={{ color: "#475569" }}>
    {highCreditAmountAccuracy.recommendation}
  </div>

              <div
                style={{
                  marginTop: "14px",
                  display: "flex",
                  gap: "10px",
                }}
              >
                <button
                  type="button"
                  onClick={() => handleGenerateAccountDispute(account)}
                  style={{
                    padding: "12px 18px",
                    borderRadius: "12px",
                    border: "none",
                    background: "#D4AF37",
                    color: "white",
                    fontWeight: "700",
                    cursor: "pointer",
                  }}
                >
                  Generate Dispute Letter
                </button>
                {account.generated_letter_id ? (
                  <>
                    <button
                      type="button"
                      onClick={() =>
                        window.open(
                          `/letters?letter_id=${account.generated_letter_id}`,
                          "_blank"
                        )
                      }
                      style={{
                        padding: "12px 18px",
                        borderRadius: "12px",
                        border: "none",
                        background: "#16a34a",
                        color: "white",
                        fontWeight: "700",
                        cursor: "pointer",
                      }}
                    >
                      View Letter
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        window.open(
                          getLetterPdfUrl(account.generated_letter_id),
                          "_blank"
                        )
                      }
                      style={{
                        padding: "12px 18px",
                        borderRadius: "12px",
                        border: "none",
                        background: "#D4AF37",
                        color: "white",
                        fontWeight: "700",
                        cursor: "pointer",
                      }}
                    >
                      Download PDF
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {error ? (
        <div style={errorStyle}>
          {error}
        </div>
      ) : null}

      {successMessage ? (
        <div style={successStyle}>
          {successMessage}
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1.4fr",
          gap: "24px",
          alignItems: "start",
        }}
      >
        <div style={panelStyle}>
          <div style={panelTitleStyle}>
            Upload Credit Report
          </div>

          <form onSubmit={handleUpload}>
            <div style={labelStyle}>
              Client
            </div>

            <select
              value={selectedClientId}
              onChange={(e) =>
                setSelectedClientId(e.target.value)
              }
              style={inputStyle}
            >
              <option value="">
                Select Client
              </option>

              {clients.map((client) => (
                <option
                  key={client.id}
                  value={client.id}
                >
                  {client.full_name}
                </option>
              ))}
            </select>

            <div style={labelStyle}>
              Bureau
            </div>

            <select
              value={bureau}
              onChange={(e) =>
                setBureau(e.target.value)
              }
              style={inputStyle}
            >
              <option>
                Experian
              </option>

              <option>
                Equifax
              </option>

              <option>
                TransUnion
              </option>
            </select>

            <div style={labelStyle}>
              File
            </div>

            <input
              id="credit-report-file"
              type="file"
              onChange={(e) =>
                setSelectedFile(
                  e.target.files?.[0] || null
                )
              }
              style={inputStyle}
            />

            <button
              type="submit"
              style={buttonStyle}
            >
              {uploading
                ? "Uploading..."
                : "Upload Credit Report"}
            </button>
         
            <button
              type="button"
              onClick={handleDeleteImportedAccounts}
              disabled={!selectedClientId || loading || uploading}
              style={{
                ...buttonStyle,
                marginTop: "12px",
                background:
                  !selectedClientId || loading || uploading
                    ? "#94a3b8"
                    : "#dc2626",
                cursor:
                  !selectedClientId || loading || uploading
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              {loading
                ? "Please Wait..."
                : "Delete Imported Accounts"}
            </button>           

          </form>
        </div>

      </div>
      
      <AccountInspector
        open={inspectorOpen}
        account={selectedAccount}
        generating={uploading}
        onGenerateLetter={handleGenerateAccountDispute}
        onViewLetter={(letterId) =>
          window.open(`/letters?letter_id=${letterId}`, "_blank")
        }
        onDownloadPdf={(letterId) =>
          window.open(getLetterPdfUrl(letterId), "_blank")
        }
        onClose={() => {
          setInspectorOpen(false);
          setSelectedAccount(null);
        }}
      />

    </div>
  );
}

const panelStyle = {
  background: "white",
  borderRadius: "24px",
  padding: "24px",
  border: "1px solid #e5e7eb",
};

const panelTitleStyle = {
  fontSize: "34px",
  fontWeight: "800",
  color: "#0f172a",
  marginBottom: "24px",
};

const labelStyle = {
  fontSize: "16px",
  fontWeight: "700",
  marginBottom: "10px",
  marginTop: "18px",
  color: "#334155",
};

const inputStyle = {
  width: "100%",
  padding: "16px",
  borderRadius: "16px",
  border: "1px solid #d1d5db",
  fontSize: "16px",
  boxSizing: "border-box",
};

const buttonStyle = {
  width: "100%",
  padding: "18px",
  borderRadius: "18px",
  border: "none",
  background: "#0f172a",
  color: "white",
  fontSize: "18px",
  fontWeight: "700",
  cursor: "pointer",
  marginTop: "24px",
};

const errorStyle = {
  border: "2px solid #ef4444",
  background: "white",
  padding: "18px",
  borderRadius: "18px",
  marginBottom: "20px",
};

const successStyle = {
  border: "2px solid #22c55e",
  background: "white",
  padding: "18px",
  borderRadius: "18px",
  marginBottom: "20px",
};
